import React, { useEffect, useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useLocation } from 'react-router-dom';
import { useHistory } from "react-router-dom";

// Keep your existing helpers unchanged
const decodeBase64Image = (base64String) => {
  if (!base64String) return null;
  base64String = base64String.trim();
  if (base64String.startsWith('data:image')) return base64String;
  const isPNG = base64String.startsWith('iVBORw0KGgo');
  const isJPEG = base64String.startsWith('/9j/');
  if (isPNG) return `data:image/png;base64,${base64String}`;
  if (isJPEG) return `data:image/jpeg;base64,${base64String}`;
  return `data:image/png;base64,${base64String}`;
};

const transformInvoiceData = (record, logo) => {
  if (!record) return null;
  const items = record.items || record.products || record.tax_invoice_line_items || [];
  const parseAmount = (value) => parseFloat(String(value || 0).replace(/,/g, ''));
  return {
    billTo: {
      name: record.bill_to?.name ||
        "Customer",
      address: `${record.bill_to?.address || ''}, ${record.bill_to?.location || ''}, ${record.bill_to?.state ||
        ''}`,
      gstin: record.bill_to?.gstin || ""
    },
    billFrom: {
      name: record.bill_from?.name ||
        "Your Company",
      address: record.bill_from?.address || "",
      gstin: record.bill_from?.gstin ||
        "",
      state: record.bill_from?.state || "",
      pincode: record.bill_from?.pincode ||
        "",
      location: record.bill_from?.location || ""
    },
    invoice: {
      number: record.invoice_number ||
        "",
      date: record.invoice_date || new Date().toLocaleDateString('en-GB'),
      documentType: record.inv_det_document_type ||
        "",
      originalDate: record.original_invoice_date || null,
      originalNumber: record.original_invoice_number ||
        null,
      reverseCharge: record.reverse_charge || "No"
    },
    company_logo: logo ||
      decodeBase64Image(record.company?.logo),
    dispatchFrom: record.dispatch_from || null,
    shipTo: record.ship_to ||
      null,
    placeOfSupply: record.place_of_supply || record.bill_to?.state || "",
    items: items.map((item, i) => ({
      id: i + 1,
      name: item.description || item.name || "Item",
      line_item_description: item.line_item_description || "",
      // description: item.additional_description || "",
      hsn: item.hsn_sac || "",
      // qty: `${parseFloat(item.qty || 1).toFixed(2)} Nos`,
      qty: `${parseFloat(item.qty || 1).toFixed(2)} ${item.unit || 'Nos'}`,
      rate: parseAmount(item.rate || 0),
      gst_rate: item.gst_rate || "18",
      discount: item.discount || "0",
      amount: parseAmount(item.amount
        || 0)
    })),
    subtotal: parseAmount(record.total_taxable || items.reduce((sum, item) => sum + parseAmount(item.amount), 0)),
    cgst: parseAmount(record.cgst_total || record.cgst || 0),
    sgst: parseAmount(record.sgst_total || record.sgst || 0),
    igst: parseAmount(record.igst_total || record.igst || 0),
    cgstPercent: record.cgst_percent ||
      "9",
    sgstPercent: record.sgst_percent || "9",
    otherCharges: parseAmount(record.other_charges || 0),
    roundOff: parseAmount(record.round_off || 0),
    discount: parseAmount(record.discount || record.total_discount || 0),
    total: parseAmount(record.total || items.reduce((sum, item) => sum + parseAmount(item.amount), 0)),
    bankInfo: {
      bankName: record.bank_info?.bank_name ||
        "",
      accountholdername: record.bank_info?.account_holder_name || "",
      accountNumber: record.bank_info?.account_number ||
        "",
      ifscCode: record.bank_info?.ifsc_code || "",
    },

    in_words: record.in_words ||
      "Five Thousand Only"
  };
};

// --- FONT MERGING UTILITY (Critical for Font Fix) ---
const mergeFonts = (defaultSettings, userSettings) => {
  return Object.keys(defaultSettings).reduce((acc, key) => {
    // Deep merge each category (e.g., companyName, invoiceTitle)
    acc[key] = { ...defaultSettings[key], ...userSettings[key] };
    return acc;
  }, {});
};

const ResizableHeader = ({ children, columnKey, onResize }) => {
  const headerRef = useRef(null);
  useEffect(() => {
    const th = headerRef.current?.parentElement;
    if (!th) return;

    const resizer = document.createElement('div');
    resizer.style.position = 'absolute';
    resizer.style.right = '0';
    resizer.style.top = '0';
    resizer.style.width = '6px';
    resizer.style.height = '100%';
    resizer.style.cursor = 'col-resize';
    resizer.style.userSelect = 'none';
    resizer.style.zIndex = '10';
    resizer.innerHTML = `<div style="width:1px;height:100%;background:#aaa;margin-left:auto;"></div>`;

    th.style.position = 'relative';
    th.appendChild(resizer);

    let startX, startWidth;

    const onMouseDown =
      (e) => {
        e.preventDefault();
        startX = e.clientX;
        startWidth = th.offsetWidth;
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      };

    const onMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(40, startWidth + diff);
      // min 40px
      th.style.width = `${newWidth}px`;
      th.style.maxWidth = `${newWidth}px`;
      if (onResize) onResize(columnKey, newWidth);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    resizer.addEventListener('mousedown', onMouseDown);
    return () => {
      resizer.removeEventListener('mousedown', onMouseDown);
      if (th.contains(resizer)) th.removeChild(resizer);
    };
  }, [columnKey, onResize]);
  return <div ref={headerRef}>{children}</div>;
};

// ClassicTemplate Component (unchanged)
// const ClassicTemplate = ({
//   data,
//   columnVisibility,
//   headerLabels,
//   logoSize,
//   logoPosition = { x: 350, y: 40 },
//   signatureImage,
//   signaturePosition = { x: 500, y: 900 },
//   onLogoDrag,
//   onSignatureDrag,
//   fontSettings,
//   columnWidths = {},
//   onColumnResize,
// }) => {
//   const defaultFontSettings = {
//     companyName: { size: 24, family: 'Arial', color: '#333333', fontWeight: 'normal' },
//     invoiceTitle: { size: 32, family: 'Arial', color: '#333333', fontWeight: 'bold' },
//     sectionHeaders: { size: 12, family: 'Arial', color: '#666666', fontWeight: 'bold' },
//     tableHeaders: { size: 11, family: 'Arial', color: '#000000', fontWeight: 'bold' },
//     tableContent: { size: 12, family: 'Arial', color: '#000000', fontWeight: 'normal' },
//     totals: { size: 14, family: 'Arial', color: '#333333', fontWeight: 'bold' }
//   };

//   const fonts = mergeFonts(defaultFontSettings, fontSettings);

//   const numberToWords = (num) => {
//     if (num === 0) return 'Zero Rupees Only';

//     const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
//       'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
//     const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

//     const convertBelowThousand = (n) => {
//       if (n === 0) return '';
//       if (n < 20) return ones[n];
//       if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
//       return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
//     };

//     let str = '';
//     num = Math.round(num * 100) / 100; // handle floating point precisely
//     const rupees = Math.floor(num);
//     const paise = Math.round((num - rupees) * 100);

//     if (rupees > 0) {
//       if (rupees >= 10000000) {
//         str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
//       }
//       if (rupees % 10000000 >= 100000) {
//         str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
//       }
//       if (rupees % 100000 >= 1000) {
//         str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
//       }
//       str += convertBelowThousand(rupees % 1000);
//       str += ' Rupees';
//     }

//     if (paise > 0) {
//       str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
//     }

//     return str.trim() + ' Only';
//   };

//   const totalInWords = numberToWords(parseFloat(data.total || 0));
//   const formatCurrency = (value) => {
//     if (value === null || value === undefined || value === '') return '₹0.00';

//     // Remove any existing commas and convert to number
//     const num = parseFloat(String(value).replace(/,/g, ''));

//     if (isNaN(num)) return '₹0.00';

//     // Format with commas and 2 decimal places (Indian style)
//     return '₹' + num.toLocaleString('en-IN', {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     });
//   };


//   return (
//     <div style={{
//       fontFamily: fonts.tableContent.family,
//       padding: '40px',
//       backgroundColor: '#fff',
//       border: '1px solid #e0e0e0',
//       minHeight: '400px'
//     }}>
//       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', position: 'relative', minHeight: '150px' }}>
//         <div style={{ flex: '0 0 50%', maxWidth: '50%' }}>
//           <h1 style={{
//             margin: '0 0 10px 0',
//             fontSize: `${fonts.companyName.size}px`,
//             fontFamily: fonts.companyName.family,
//             fontWeight: fonts.companyName.fontWeight,
//             color: fonts.companyName.color
//           }}>
//             {data.billFrom?.name}
//           </h1>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.address}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.state}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.pincode}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.location}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billFrom?.gstin}</p>
//         </div>

//         <div style={{ flex: '0 0 50%', maxWidth: '50%', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '15px', paddingTop: data.company_logo ? '80px' : '0' }}>
//           {data.company_logo && (
//             <div
//               style={{
//                 position: 'absolute',
//                 left: `${logoPosition.x}px`,
//                 top: `${logoPosition.y}px`,
//                 cursor: 'move',
//                 zIndex: 100,
//                 border: '2px dashed transparent',
//                 transition: 'border 0.2s'
//               }}
//               draggable
//               onDragEnd={onLogoDrag}
//               onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #94a3b8'}
//               onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
//             >
//               <img
//                 src={data.company_logo}
//                 alt="Company Logo"
//                 style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
//               />
//             </div>
//           )}

//           <div style={{ marginTop: data.company_logo ? '10px' : '0' }}>
//             <h2 style={{
//               margin: '0 0 8px 0',
//               fontSize: `${fonts.invoiceTitle.size}px`,
//               fontFamily: fonts.invoiceTitle.family,
//               fontWeight: fonts.invoiceTitle.fontWeight,
//               color: fonts.invoiceTitle.color
//             }}>
//               {data.invoice?.documentType || 'INVOICE'}
//             </h2>
//             <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{data.invoice?.number}</p>
//           </div>
//         </div>
//       </div>

//       {data.dispatchFrom && (
//         <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f8f8', border: '1px solid #ddd' }}>
//           <h3 style={{
//             margin: '0 0 5px 0',
//             fontSize: `${fonts.sectionHeaders.size}px`,
//             fontFamily: fonts.sectionHeaders.family,
//             fontWeight: fonts.sectionHeaders.fontWeight,
//             color: fonts.sectionHeaders.color
//           }}>DISPATCH FROM</h3>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#333' }}>{data.dispatchFrom?.name}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.dispatchFrom?.address}</p>
//         </div>
//       )}

//       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
//         <div style={{ width: '48%' }}>
//           <h3 style={{
//             margin: '0 0 10px 0',
//             fontSize: `${fonts.sectionHeaders.size}px`,
//             fontFamily: fonts.sectionHeaders.family,
//             fontWeight: fonts.sectionHeaders.fontWeight,
//             color: fonts.sectionHeaders.color
//           }}>BILL TO</h3>
//           <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.billTo?.name}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billTo?.address}</p>
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billTo?.gstin}</p>
//         </div>
//         {data.shipTo && (
//           <div style={{ width: '48%' }}>
//             <h3 style={{
//               margin: '0 0 10px 0',
//               fontSize: `${fonts.sectionHeaders.size}px`,
//               fontFamily: fonts.sectionHeaders.family,
//               fontWeight: fonts.sectionHeaders.fontWeight,
//               color: fonts.sectionHeaders.color
//             }}>SHIP TO</h3>
//             <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.shipTo?.name}</p>
//             <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.shipTo?.address}</p>
//           </div>
//         )}
//       </div>

//       <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f8f8' }}>
//         {data.invoice?.documentType === "Invoice" && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Invoice Date:</span> {data.invoice?.date}
//           </p>
//         )}
//         {data.invoice?.documentType === "Bill" && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Bill Date:</span> {data.invoice?.date}
//           </p>
//         )}
//         {data.invoice?.documentType === "Credit Note" && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Credit Note Date:</span> {data.invoice?.date}
//           </p>
//         )}
//         {data.invoice?.documentType === "Debit Note" && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Debit Note Date:</span> {data.invoice?.date}
//           </p>
//         )}
//         {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Original Invoice Date:</span>{' '}
//             {data.invoice?.originalDate}
//           </p>
//         )}
//         {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//             <span style={{ fontWeight: 'bold' }}>Original Invoice Number:</span>{' '}
//             {data.invoice?.originalNumber}
//           </p>
//         )}
//         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
//           <span style={{ fontWeight: 'bold' }}>Place of Supply:</span> {data.placeOfSupply}
//         </p>
//         {data.invoice?.reverseCharge === 'Yes' && (
//           <p style={{ margin: '2px 0', fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
//             Reverse Charge Applicable
//           </p>
//         )}
//       </div>

//       <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
//         <colgroup>
//           {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
//           {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
//           {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
//           {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
//           {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
//           {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
//           {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
//           {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
//         </colgroup>

//         <thead>
//           <tr style={{ backgroundColor: '#f5f5f5' }}>
//             {columnVisibility.srNo && (
//               <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.item && (
//               <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.hsn && (
//               <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.qty && (
//               <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.rate && (
//               <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.gst && (
//               <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.discount && (
//               <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
//               </th>
//             )}
//             {columnVisibility.amount && (
//               <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                 <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
//               </th>
//             )}
//           </tr>
//         </thead>

//         <tbody>
//           {data.items?.map((item, index) => (
//             <tr key={item.id}>
//               {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
//               {columnVisibility.item && (
//                 <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
//                   <div style={{ fontWeight: 'bold' }}>{item.name}</div>
//                   {item.description && <div style={{ fontSize: '11px', color: '#666' }}>{item.description}</div>}
//                 </td>
//               )}
//               {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
//               {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
//               {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>₹{parseFloat(item.rate).toFixed(2)}</td>}
//               {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
//               {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.discount}</td>}
//               {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>₹{parseFloat(item.amount).toFixed(2)}</td>}
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       <div style={{ textAlign: 'right', marginTop: '20px' }}>
//         <div style={{ display: 'inline-block', minWidth: '250px' }}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{
//               fontSize: `${fonts.totals.size - 2}px`,
//               fontFamily: fonts.totals.family,
//               color: '#666'
//             }}>Subtotal</span>
//             <span style={{
//               fontSize: `${fonts.totals.size - 2}px`,
//               fontFamily: fonts.totals.family,
//               fontWeight: fonts.totals.fontWeight
//             }}>
//               ₹{parseFloat(data.subtotal).toFixed(2)}</span>
//           </div>
//           {data.cgst > 0 && (
//             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 color: '#666'
//               }}>CGST ({data.cgstPercent}%)</span>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 fontWeight: fonts.totals.fontWeight
//               }}>₹{parseFloat(data.cgst).toFixed(2)}</span>
//             </div>
//           )}
//           {data.sgst > 0 && (
//             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 color: '#666'
//               }}>SGST ({data.sgstPercent}%)</span>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 fontWeight: fonts.totals.fontWeight
//               }}>₹{parseFloat(data.sgst).toFixed(2)}</span>
//             </div>
//           )}
//           {data.igst > 0 && (
//             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 color: '#666'
//               }}>IGST</span>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 fontWeight: fonts.totals.fontWeight
//               }}>₹{parseFloat(data.igst).toFixed(2)}</span>
//             </div>
//           )}
//           {data.otherCharges > 0 && (
//             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 color: '#666'
//               }}>Other Charges</span>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 fontWeight: fonts.totals.fontWeight
//               }}>₹{parseFloat(data.otherCharges).toFixed(2)}</span>
//             </div>
//           )}
//           {data.roundOff != null && data.roundOff !== 0 && (
//             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 color: '#666'
//               }}>Round Off</span>
//               <span style={{
//                 fontSize: `${fonts.totals.size - 2}px`,
//                 fontFamily: fonts.totals.family,
//                 fontWeight: fonts.totals.fontWeight,
//                 color: data.roundOff < 0 ? '#dc2626' : '#333'  // Red if negative
//               }}>
//                 ₹{parseFloat(data.roundOff).toFixed(2)}
//               </span>
//             </div>
//           )}
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{
//               fontSize: `${fonts.totals.size - 2}px`,
//               fontFamily: fonts.totals.family,
//               color: '#666'
//             }}>Discount</span>
//             <span style={{
//               fontSize: `${fonts.totals.size - 2}px`,
//               fontFamily: fonts.totals.family,
//               fontWeight: fonts.totals.fontWeight
//             }}>₹{data.discount}</span>
//           </div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #333', marginTop: '8px' }}>
//             <span style={{
//               fontSize: `${fonts.totals.size}px`,
//               fontFamily: fonts.totals.family,
//               fontWeight: fonts.totals.fontWeight,
//               color: fonts.totals.color
//             }}>Total</span>
//             <span style={{
//               fontSize: `${fonts.totals.size}px`,
//               fontFamily: fonts.totals.family,
//               fontWeight: fonts.totals.fontWeight,
//               color: fonts.totals.color
//             }}>₹{parseFloat(data.total).toFixed(2)}</span>
//           </div>
//           <div style={{ marginTop: '12px', textAlign: 'right', padding: '10px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
//             <span style={{
//               fontSize: '13px',
//               fontFamily: fonts.totals.family,
//               fontStyle: 'italic',
//               color: '#444'
//             }}>
//               <strong>Amount in Words:</strong> {totalInWords}
//             </span>
//           </div>
//         </div>
//       </div>

//       <div style={{ textAlign: 'left', marginTop: '20px' }}>
//         <div style={{ display: 'inline-block', minWidth: '250px' }}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{ fontSize: '12px', color: '#666' }}>Bank</span>
//             <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.bankName}</span>
//           </div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{ fontSize: '12px', color: '#666' }}>Account Holder Name</span>
//             <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountholdername}</span>
//           </div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{ fontSize: '12px', color: '#666' }}>Account Number</span>
//             <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountNumber}</span>
//           </div>
//           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
//             <span style={{ fontSize: '12px', color: '#666' }}>IFSC Code</span>
//             <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.ifscCode}</span>
//           </div>
//         </div>
//       </div>

//       {/* Signature Section - Fixed at bottom with proper spacing */}
//       <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
//         {signatureImage ? (
//           <div
//             style={{
//               position: 'relative',
//               left: `${signaturePosition.x}px`,
//               cursor: 'move',
//               display: 'inline-block',
//               border: '2px dashed transparent',
//               transition: 'border 0.2s'
//             }}
//             draggable
//             onDragEnd={onSignatureDrag}
//             onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
//             onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
//           >
//             <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
//             <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
//               Authorized Signatory
//             </div>
//           </div>
//         ) : (
//           <div style={{ textAlign: 'right' }}>
//             <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
//               <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

const ClassicTemplate = ({
  data,
  columnVisibility,
  headerLabels,
  logoSize,
  logoPosition = { x: 350, y: 40 },
  signatureImage,
  signaturePosition = { x: 500, y: 900 },
  onLogoDrag,
  onSignatureDrag,
  fontSettings,
  columnWidths = {},
  onColumnResize,
}) => {
  const defaultFontSettings = {
    companyName: { size: 24, family: 'Arial', color: '#333333', fontWeight: 'normal' },
    invoiceTitle: { size: 32, family: 'Arial', color: '#333333', fontWeight: 'bold' },
    sectionHeaders: { size: 12, family: 'Arial', color: '#666666', fontWeight: 'bold' },
    tableHeaders: { size: 11, family: 'Arial', color: '#000000', fontWeight: 'bold' },
    tableContent: { size: 12, family: 'Arial', color: '#000000', fontWeight: 'normal' },
    totals: { size: 14, family: 'Arial', color: '#333333', fontWeight: 'bold' }
  };
  const fonts = mergeFonts(defaultFontSettings, fontSettings);

  // Safe currency formatter – handles values with or without commas from backend
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '₹0.00';

    // Remove commas if the value is a string (common when backend sends formatted numbers)
    const num = parseFloat(String(value).replace(/,/g, ''));

    if (isNaN(num)) return '₹0.00';

    // Indian style comma formatting with 2 decimal places
    return '₹' + num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
    };

    let str = '';
    const cleanNum = parseFloat(String(num).replace(/,/g, '')) || 0;
    const rupees = Math.floor(cleanNum);
    const paise = Math.round((cleanNum - rupees) * 100);

    if (rupees > 0) {
      if (rupees >= 10000000) {
        str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
      }
      if (rupees % 10000000 >= 100000) {
        str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
      }
      if (rupees % 100000 >= 1000) {
        str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
      }
      str += convertBelowThousand(rupees % 1000);
      str += ' Rupees';
    }

    if (paise > 0) {
      str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
    }

    return str.trim() + ' Only';
  };

  const totalInWords = numberToWords(data.total);
  return (
    <div style={{
      fontFamily: fonts.tableContent.family,
      padding: '40px',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      minHeight: '400px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', position: 'relative', minHeight: '150px' }}>
        <div style={{ flex: '0 0 50%', maxWidth: '50%' }}>
          <h1 style={{
            margin: '0 0 10px 0',
            fontSize: `${fonts.companyName.size}px`,
            fontFamily: fonts.companyName.family,
            fontWeight: fonts.companyName.fontWeight,
            color: fonts.companyName.color
          }}>
            {data.billFrom?.name}
          </h1>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.address}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.state}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.pincode}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.location}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billFrom?.gstin}</p>
        </div>
        <div style={{ flex: '0 0 50%', maxWidth: '50%', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '15px', paddingTop: data.company_logo ? '80px' : '0' }}>
          {data.company_logo && (
            <div
              style={{
                position: 'absolute',
                right: '0',
                top: '0',
                zIndex: 100
              }}
            >
              <img
                src={data.company_logo}
                alt="Company Logo"
                style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
              />
            </div>
          )}
          <div style={{ marginTop: data.company_logo ? '10px' : '0' }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: `${fonts.invoiceTitle.size}px`,
              fontFamily: fonts.invoiceTitle.family,
              fontWeight: fonts.invoiceTitle.fontWeight,
              color: fonts.invoiceTitle.color
            }}>
              {data.invoice?.documentType || 'INVOICE'}
            </h2>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{data.invoice?.number}</p>
          </div>
        </div>
      </div>

      {data.dispatchFrom && (
        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f8f8', border: '1px solid #ddd' }}>
          <h3 style={{
            margin: '0 0 5px 0',
            fontSize: `${fonts.sectionHeaders.size}px`,
            fontFamily: fonts.sectionHeaders.family,
            fontWeight: fonts.sectionHeaders.fontWeight,
            color: fonts.sectionHeaders.color
          }}>DISPATCH FROM</h3>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#333' }}>{data.dispatchFrom?.name}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.dispatchFrom?.address}</p>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ width: '48%' }}>
          <h3 style={{
            margin: '0 0 10px 0',
            fontSize: `${fonts.sectionHeaders.size}px`,
            fontFamily: fonts.sectionHeaders.family,
            fontWeight: fonts.sectionHeaders.fontWeight,
            color: fonts.sectionHeaders.color
          }}>BILL TO</h3>
          <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.billTo?.name}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billTo?.address}</p>
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billTo?.gstin}</p>
        </div>
        {data.shipTo && (
          <div style={{ width: '48%' }}>
            <h3 style={{
              margin: '0 0 10px 0',
              fontSize: `${fonts.sectionHeaders.size}px`,
              fontFamily: fonts.sectionHeaders.family,
              fontWeight: fonts.sectionHeaders.fontWeight,
              color: fonts.sectionHeaders.color
            }}>SHIP TO</h3>
            <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.shipTo?.name}</p>
            <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.shipTo?.address}</p>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f8f8' }}>
        {data.invoice?.documentType === "Invoice" && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Invoice Date:</span> {data.invoice?.date}
          </p>
        )}
        {data.invoice?.documentType === "Bill" && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Bill Date:</span> {data.invoice?.date}
          </p>
        )}
        {data.invoice?.documentType === "Credit Note" && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Credit Note Date:</span> {data.invoice?.date}
          </p>
        )}
        {data.invoice?.documentType === "Debit Note" && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Debit Note Date:</span> {data.invoice?.date}
          </p>
        )}
        {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
          </p>
        )}
        {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
            <span style={{ fontWeight: 'bold' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
          </p>
        )}
        <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
          <span style={{ fontWeight: 'bold' }}>Place of Supply:</span> {data.placeOfSupply}
        </p>
        {data.invoice?.reverseCharge === 'Yes' && (
          <p style={{ margin: '2px 0', fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
            Reverse Charge Applicable
          </p>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
        <colgroup>
          {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
          {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
          {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
          {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
          {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
          {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
          {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
          {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            {columnVisibility.srNo && (
              <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                #
              </th>
            )}
            {columnVisibility.item && (
              <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.item}
              </th>
            )}
            {columnVisibility.hsn && (
              <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.hsn}
              </th>
            )}
            {columnVisibility.qty && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.qty}
              </th>
            )}
            {columnVisibility.rate && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.rate}
              </th>
            )}
            {columnVisibility.gst && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.gst}
              </th>
            )}
            {columnVisibility.discount && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.discount}
              </th>
            )}
            {columnVisibility.amount && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                {headerLabels.amount}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.items?.map((item, index) => (
            <tr key={item.id}>
              {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
              {columnVisibility.item && (
                <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                  <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                  {item.line_item_description && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{item.line_item_description}</div>}
                </td>
              )}
              {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
              {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
              {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.rate)}
              </td>}
              {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
              {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.discount)}
              </td>}
              {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.amount)}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ textAlign: 'right', marginTop: '20px' }}>
        <div style={{ display: 'inline-block', minWidth: '250px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Subtotal</span>
            <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
              {formatCurrency(data.subtotal)}
            </span>
          </div>
          {data.cgst > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>CGST ({data.cgstPercent}%)</span>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
                {formatCurrency(data.cgst)}
              </span>
            </div>
          )}
          {data.sgst > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>SGST ({data.sgstPercent}%)</span>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
                {formatCurrency(data.sgst)}
              </span>
            </div>
          )}
          {data.igst > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>IGST</span>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
                {formatCurrency(data.igst)}
              </span>
            </div>
          )}
          {data.otherCharges > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Other Charges</span>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
                {formatCurrency(data.otherCharges)}
              </span>
            </div>
          )}
          {data.roundOff != null && data.roundOff !== 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Round Off</span>
              <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: data.roundOff < 0 ? '#dc2626' : '#333' }}>
                {formatCurrency(data.roundOff)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Discount</span>
            <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
              {formatCurrency(data.discount)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #333', marginTop: '8px' }}>
            <span style={{ fontSize: `${fonts.totals.size}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: fonts.totals.color }}>Total</span>
            <span style={{ fontSize: `${fonts.totals.size}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: fonts.totals.color }}>
              {formatCurrency(data.total)}
            </span>
          </div>
          <div style={{ marginTop: '12px', textAlign: 'right', padding: '10px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
            <span style={{ fontSize: '13px', fontFamily: fonts.totals.family, fontStyle: 'italic', color: '#444' }}>
              <strong>Amount in Words:</strong> {totalInWords}
            </span>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'left', marginTop: '20px' }}>
        <div style={{ display: 'inline-block', minWidth: '250px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Bank</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.bankName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Account Holder Name</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountholdername}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Account Number</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountNumber}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
            <span style={{ fontSize: '12px', color: '#666' }}>IFSC Code</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.ifscCode}</span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
        {signatureImage ? (
          <div
            style={{
              position: 'relative',
              left: `${signaturePosition.x}px`,
              cursor: 'move',
              display: 'inline-block',
              border: '2px dashed transparent',
              transition: 'border 0.2s'
            }}
            draggable
            onDragEnd={onSignatureDrag}
            onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
            onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
          >
            <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
              Authorized Signatory
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  // return (
  //   <div style={{
  //     fontFamily: fonts.tableContent.family,
  //     padding: '40px',
  //     backgroundColor: '#fff',
  //     border: '1px solid #e0e0e0',
  //     minHeight: '400px'
  //   }}>
  //     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', position: 'relative', minHeight: '150px' }}>
  //       <div style={{ flex: '0 0 50%', maxWidth: '50%' }}>
  //         <h1 style={{
  //           margin: '0 0 10px 0',
  //           fontSize: `${fonts.companyName.size}px`,
  //           fontFamily: fonts.companyName.family,
  //           fontWeight: fonts.companyName.fontWeight,
  //           color: fonts.companyName.color
  //         }}>
  //           {data.billFrom?.name}
  //         </h1>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.address}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.state}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.pincode}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billFrom?.location}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billFrom?.gstin}</p>
  //       </div>
  //       <div style={{ flex: '0 0 50%', maxWidth: '50%', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '15px', paddingTop: data.company_logo ? '80px' : '0' }}>
  //          {data.company_logo && (
  //           <div
  //             style={{
  //               position: 'absolute',
  //               left: `${logoPosition.x}px`,
  //               top: `${logoPosition.y}px`,
  //               cursor: 'move',
  //               zIndex: 100,
  //               border: '2px dashed transparent',
  //               transition: 'border 0.2s'
  //             }}
  //             draggable
  //             onDragEnd={onLogoDrag}
  //             onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #94a3b8'}
  //             onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //           >
  //             <img
  //               src={data.company_logo}
  //               alt="Company Logo"
  //               style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
  //             />
  //           </div>
  //         )}
  //         <div style={{ marginTop: data.company_logo ? '10px' : '0' }}>
  //           <h2 style={{
  //             margin: '0 0 8px 0',
  //             fontSize: `${fonts.invoiceTitle.size}px`,
  //             fontFamily: fonts.invoiceTitle.family,
  //             fontWeight: fonts.invoiceTitle.fontWeight,
  //             color: fonts.invoiceTitle.color
  //           }}>
  //             {data.invoice?.documentType || 'INVOICE'}
  //           </h2>
  //           <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{data.invoice?.number}</p>
  //         </div>
  //       </div>
  //     </div>

  //     {data.dispatchFrom && (
  //       <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f8f8', border: '1px solid #ddd' }}>
  //         <h3 style={{
  //           margin: '0 0 5px 0',
  //           fontSize: `${fonts.sectionHeaders.size}px`,
  //           fontFamily: fonts.sectionHeaders.family,
  //           fontWeight: fonts.sectionHeaders.fontWeight,
  //           color: fonts.sectionHeaders.color
  //         }}>DISPATCH FROM</h3>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#333' }}>{data.dispatchFrom?.name}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.dispatchFrom?.address}</p>
  //       </div>
  //     )}

  //     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
  //       <div style={{ width: '48%' }}>
  //         <h3 style={{
  //           margin: '0 0 10px 0',
  //           fontSize: `${fonts.sectionHeaders.size}px`,
  //           fontFamily: fonts.sectionHeaders.family,
  //           fontWeight: fonts.sectionHeaders.fontWeight,
  //           color: fonts.sectionHeaders.color
  //         }}>BILL TO</h3>
  //         <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.billTo?.name}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.billTo?.address}</p>
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>GSTIN: {data.billTo?.gstin}</p>
  //       </div>
  //       {data.shipTo && (
  //         <div style={{ width: '48%' }}>
  //           <h3 style={{
  //             margin: '0 0 10px 0',
  //             fontSize: `${fonts.sectionHeaders.size}px`,
  //             fontFamily: fonts.sectionHeaders.family,
  //             fontWeight: fonts.sectionHeaders.fontWeight,
  //             color: fonts.sectionHeaders.color
  //           }}>SHIP TO</h3>
  //           <p style={{ margin: '2px 0', fontSize: '13px', fontWeight: 'bold', color: '#333' }}>{data.shipTo?.name}</p>
  //           <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>{data.shipTo?.address}</p>
  //         </div>
  //       )}
  //     </div>

  //     <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f8f8' }}>
  //       {data.invoice?.documentType === "Invoice" && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Invoice Date:</span> {data.invoice?.date}
  //         </p>
  //       )}
  //       {data.invoice?.documentType === "Bill" && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Bill Date:</span> {data.invoice?.date}
  //         </p>
  //       )}
  //       {data.invoice?.documentType === "Credit Note" && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Credit Note Date:</span> {data.invoice?.date}
  //         </p>
  //       )}
  //       {data.invoice?.documentType === "Debit Note" && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Debit Note Date:</span> {data.invoice?.date}
  //         </p>
  //       )}
  //       {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
  //         </p>
  //       )}
  //       {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //           <span style={{ fontWeight: 'bold' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
  //         </p>
  //       )}
  //       <p style={{ margin: '2px 0', fontSize: '12px', color: '#666' }}>
  //         <span style={{ fontWeight: 'bold' }}>Place of Supply:</span> {data.placeOfSupply}
  //       </p>
  //       {data.invoice?.reverseCharge === 'Yes' && (
  //         <p style={{ margin: '2px 0', fontSize: '12px', color: '#d32f2f', fontWeight: 'bold' }}>
  //           Reverse Charge Applicable
  //         </p>
  //       )}
  //     </div>

  //     <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
  //       <colgroup>
  //         {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
  //         {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
  //         {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
  //         {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
  //         {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
  //         {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
  //         {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
  //         {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
  //       </colgroup>
  //       <thead>
  //         <tr style={{ backgroundColor: '#f5f5f5' }}>
  //           {columnVisibility.srNo && (
  //             <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.item && (
  //             <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.hsn && (
  //             <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.qty && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.rate && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.gst && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.discount && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.amount && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
  //             </th>
  //           )}
  //         </tr>
  //       </thead>
  //       <tbody>
  //         {data.items?.map((item, index) => (
  //           <tr key={item.id}>
  //             {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
  //             {columnVisibility.item && (
  //               <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //                 <div style={{ fontWeight: 'bold' }}>{item.name}</div>
  //                 {item.description && <div style={{ fontSize: '11px', color: '#666' }}>{item.description}</div>}
  //               </td>
  //             )}
  //             {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
  //             {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
  //             {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.rate)}
  //             </td>}
  //             {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
  //             {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.discount)}
  //             </td>}
  //             {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.amount)}
  //             </td>}
  //           </tr>
  //         ))}
  //       </tbody>
  //     </table>

  //     <div style={{ textAlign: 'right', marginTop: '20px' }}>
  //       <div style={{ display: 'inline-block', minWidth: '250px' }}>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Subtotal</span>
  //           <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //             {formatCurrency(data.subtotal)}
  //           </span>
  //         </div>
  //         {data.cgst > 0 && (
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>CGST ({data.cgstPercent}%)</span>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //               {formatCurrency(data.cgst)}
  //             </span>
  //           </div>
  //         )}
  //         {data.sgst > 0 && (
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>SGST ({data.sgstPercent}%)</span>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //               {formatCurrency(data.sgst)}
  //             </span>
  //           </div>
  //         )}
  //         {data.igst > 0 && (
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>IGST</span>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //               {formatCurrency(data.igst)}
  //             </span>
  //           </div>
  //         )}
  //         {data.otherCharges > 0 && (
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Other Charges</span>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //               {formatCurrency(data.otherCharges)}
  //             </span>
  //           </div>
  //         )}
  //         {data.roundOff != null && data.roundOff !== 0 && (
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Round Off</span>
  //             <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: data.roundOff < 0 ? '#dc2626' : '#333' }}>
  //               {formatCurrency(data.roundOff)}
  //             </span>
  //           </div>
  //         )}
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, color: '#666' }}>Discount</span>
  //           <span style={{ fontSize: `${fonts.totals.size - 2}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight }}>
  //             {formatCurrency(data.discount)}
  //           </span>
  //         </div>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #333', marginTop: '8px' }}>
  //           <span style={{ fontSize: `${fonts.totals.size}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: fonts.totals.color }}>Total</span>
  //           <span style={{ fontSize: `${fonts.totals.size}px`, fontFamily: fonts.totals.family, fontWeight: fonts.totals.fontWeight, color: fonts.totals.color }}>
  //             {formatCurrency(data.total)}
  //           </span>
  //         </div>
  //         <div style={{ marginTop: '12px', textAlign: 'right', padding: '10px', backgroundColor: '#f8f8f8', borderRadius: '4px' }}>
  //           <span style={{ fontSize: '13px', fontFamily: fonts.totals.family, fontStyle: 'italic', color: '#444' }}>
  //             <strong>Amount in Words:</strong> {totalInWords}
  //           </span>
  //         </div>
  //       </div>
  //     </div>

  //     <div style={{ textAlign: 'left', marginTop: '20px' }}>
  //       <div style={{ display: 'inline-block', minWidth: '250px' }}>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: '12px', color: '#666' }}>Bank</span>
  //           <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.bankName}</span>
  //         </div>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: '12px', color: '#666' }}>Account Holder Name</span>
  //           <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountholdername}</span>
  //         </div>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: '12px', color: '#666' }}>Account Number</span>
  //           <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.accountNumber}</span>
  //         </div>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e0e0e0' }}>
  //           <span style={{ fontSize: '12px', color: '#666' }}>IFSC Code</span>
  //           <span style={{ fontSize: '12px', fontWeight: 'bold' }}>{data.bankInfo?.ifscCode}</span>
  //         </div>
  //       </div>
  //     </div>

  //     <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
  //       {signatureImage ? (
  //         <div
  //           style={{
  //             position: 'relative',
  //             left: `${signaturePosition.x}px`,
  //             cursor: 'move',
  //             display: 'inline-block',
  //             border: '2px dashed transparent',
  //             transition: 'border 0.2s'
  //           }}
  //           draggable
  //           onDragEnd={onSignatureDrag}
  //           onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
  //           onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //         >
  //           <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
  //           <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
  //             Authorized Signatory
  //           </div>
  //         </div>
  //       ) : (
  //         <div style={{ textAlign: 'right' }}>
  //           <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
  //             <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   </div>
  // );
};

const ModernTemplate = ({ data, columnVisibility, headerLabels, logoSize, logoPosition, signatureImage, signaturePosition, onLogoDrag, onSignatureDrag, fontSettings, columnWidths = {},
  onColumnResize, }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  // Default font settings using Classic keys
  const defaultFontSettings = {
    companyName: { size: 24, family: 'Helvetica', color: '#333333', fontWeight: '600' },
    invoiceTitle: { size: 28, family: 'Helvetica', color: '#1e40af', fontWeight: '700' },
    sectionHeaders: { size: 11, family: 'Helvetica', color: '#94a3b8', fontWeight: '600' },
    tableHeaders: { size: 11, family: 'Helvetica', color: '#64748b', fontWeight: '600' },
    tableContent: { size: 13, family: 'Helvetica', color: '#1e293b', fontWeight: 'normal' },
    totals: { size: 16, family: 'Helvetica', color: '#2563eb', fontWeight: '700' }
  };

  // FIX: Use robust font merging
  const fonts = mergeFonts(defaultFontSettings, fontSettings);




  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
    };

    let str = '';
    num = Math.round(num * 100) / 100; // handle floating point precisely
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);

    if (rupees > 0) {
      if (rupees >= 10000000) {
        str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
      }
      if (rupees % 10000000 >= 100000) {
        str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
      }
      if (rupees % 100000 >= 1000) {
        str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
      }
      str += convertBelowThousand(rupees % 1000);
      str += ' Rupees';
    }

    if (paise > 0) {
      str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
    }

    return str.trim() + ' Only';
  };

  const totalInWords = numberToWords(parseFloat(data.total || 0));

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '₹0.00';

    const num = parseFloat(String(value).replace(/,/g, ''));

    if (isNaN(num)) return '₹0.00';

    return '₹' + num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div style={{
      fontFamily: 'Helvetica, sans-serif',
      padding: '40px',
      backgroundColor: '#fff',
      minHeight: '400px',
      position: 'relative'
    }}>



      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
        <div style={{ borderLeft: '4px solid #14367eff', paddingLeft: '20px' }}>
          <h1 style={{
            margin: '0',
            fontSize: `${fonts.invoiceTitle.size}px`,
            fontFamily: fonts.invoiceTitle.family,
            color: fonts.invoiceTitle.color,
            fontWeight: fonts.invoiceTitle.fontWeight // FIX: Use merged fontWeight
          }}>
            {data.invoice?.documentType || 'INVOICE'}
          </h1>
          <p style={{ margin: '5px 0 0 0', fontSize: '16px', color: '#64748b' }}>{data.invoice?.number}</p>
          <h1 style={{
            margin: '0 0 5px 0',
            fontSize: `${fonts.companyName.size}px`,
            fontFamily: fonts.companyName.family,
            color: fonts.companyName.color,
            fontWeight: fonts.companyName.fontWeight // FIX: Use merged fontWeight
          }}>
            {data.billFrom?.name}
          </h1>
          <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.address}</p>
        </div>

        <div style={{ textAlign: 'right', minHeight: '100px' }}>
          {data.company_logo && (
            <div
              style={{
                position: 'absolute',
                right: '40px',
                top: '40px',
                zIndex: 100
              }}
            >
              <img
                src={data.company_logo}
                alt="Company Logo"
                style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
              />
            </div>
          )}
          {/* {data.company_logo && (
            <div
              style={{
                position: 'absolute',
                // Perfect top-right placement (A4-safe, clean spacing)
                right: '40px',
                top: '40px',
                cursor: 'move',
                zIndex: 100,
                padding: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.2s ease',
                // Visual drag feedback
                border: '2px dashed transparent',
              }}
              draggable
              onDragEnd={onLogoDrag}
              onMouseOver={(e) => {
                e.currentTarget.style.border = '2px dashed #3b82f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.border = '2px dashed transparent';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
              }}
            >
              <img
                src={data.company_logo}
                alt="Company Logo"
                style={{
                  width: `${logoSize}px`,
                  maxWidth: '160px',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '8px',
                }}
              />
            </div>
          )} */}
        </div>
      </div>

      {data.dispatchFrom && (
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          borderLeft: '3px solid #2563eb'
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: `${fonts.sectionHeaders.size}px`,
            fontFamily: fonts.sectionHeaders.family,
            color: fonts.sectionHeaders.color,
            fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
            letterSpacing: '1px'
          }}>
            DISPATCH FROM
          </h3>
          <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.dispatchFrom.name}</p>
          <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.dispatchFrom.address}</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
        <div>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: `${fonts.sectionHeaders.size}px`,
              fontFamily: fonts.sectionHeaders.family,
              color: fonts.sectionHeaders.color,
              fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
              letterSpacing: '1px'
            }}>FROM</h3>
            <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.billFrom?.name}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.address}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.state}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.pincode}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billFrom?.gstin}</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: `${fonts.sectionHeaders.size}px`,
              fontFamily: fonts.sectionHeaders.family,
              color: fonts.sectionHeaders.color,
              fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
              letterSpacing: '1px'
            }}>BILL TO</h3>
            <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.billTo?.name}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billTo?.address}</p>
            <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
          </div>
          {data.shipTo && (
            <div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: `${fonts.sectionHeaders.size}px`,
                fontFamily: fonts.sectionHeaders.family,
                color: fonts.sectionHeaders.color,
                fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
                letterSpacing: '1px'
              }}>SHIP TO</h3>
              <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                {data.shipTo?.name}
              </p>
              <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>
                {data.shipTo?.address}
              </p>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
            {data.invoice?.documentType === "Invoice" && (
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
                <span style={{ fontWeight: '600' }}>Invoice Date:</span> {data.invoice?.date}
              </p>
            )}
            {data.invoice?.documentType === "Bill" && (
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
                <span style={{ fontWeight: '600' }}>Bill Date:</span> {data.invoice?.date}
              </p>
            )}

            {data.invoice?.originalDate && data.invoice.originalDate !== 'null' && (
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
                <span style={{ fontWeight: '600' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
              </p>
            )}
            {data.invoice?.originalNumber && data.invoice.originalNumber !== 'null' && (
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
                <span style={{ fontWeight: '600' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
              </p>
            )}
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
              <span style={{ fontWeight: '600' }}>Place of Supply:</span> {data.placeOfSupply}
            </p>
            {data.invoice?.reverseCharge === 'Yes' && (
              <p style={{ margin: '8px 0 4px 0', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
                Reverse Charge Applicable
              </p>
            )}
          </div>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
        <colgroup>
          {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
          {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
          {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
          {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
          {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
          {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
          {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
          {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            {columnVisibility.srNo && (
              <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
              </th>
            )}
            {columnVisibility.item && (
              <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
              </th>
            )}
            {columnVisibility.hsn && (
              <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
              </th>
            )}
            {columnVisibility.qty && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
              </th>
            )}
            {columnVisibility.rate && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
              </th>
            )}
            {columnVisibility.gst && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
              </th>
            )}
            {columnVisibility.discount && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
              </th>
            )}
            {columnVisibility.amount && (
              <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
                <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.items?.map((item, index) => (
            <tr key={item.id}>
              {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
              {columnVisibility.item && (
                <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                  <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                  {item.line_item_description && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{item.line_item_description}</div>}
                </td>
              )}
              {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
              {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
              {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.rate)}
              </td>}
              {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
              {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.discount)}
              </td>}
              {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
                {formatCurrency(item.amount)}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '11px', fontWeight: '600', color: '#94a3b8', letterSpacing: '1px' }}> BANK DETAILS </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Bank:</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.bankName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Account Holder:</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.accountholdername}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Account Number:</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.accountNumber}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>IFSC Code:</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.ifscCode}</span>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Totals + Signature (Properly aligned) */}
        <div style={{ minWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {/* Totals Box */}
          <div style={{ width: '100%', maxWidth: '350px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '60px' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
              </div>
              {data.cgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
                </div>
              )}
              {data.sgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
                </div>
              )}
              {data.igst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
                </div>
              )}
              {data.otherCharges > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Other Charges</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.otherCharges)}</span>
                </div>
              )}
              {data.roundOff != null && data.roundOff !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>{formatCurrency(data.roundOff)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.discount)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #2563eb', marginTop: '12px' }}>
                <span style={{
                  fontSize: `${fonts.totals.size}px`,
                  fontFamily: fonts.totals.family,
                  color: fonts.totals.color,
                  fontWeight: fonts.totals.fontWeight
                }}>Total</span>
                <span style={{
                  fontSize: `${fonts.totals.size}px`,
                  fontFamily: fonts.totals.family,
                  color: fonts.totals.color,
                  fontWeight: fonts.totals.fontWeight
                }}>{formatCurrency(data.total)}</span>
              </div>
            </div>
          </div>

          {/* Authorized Signature - Now clearly visible at bottom right */}
          <div style={{ width: '100%', textAlign: 'right', marginTop: 'auto' }}>
            {signatureImage ? (
              <div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  cursor: 'move',
                  border: '2px dashed transparent',
                  transition: 'border 0.2s'
                }}
                draggable
                onDragEnd={onSignatureDrag}
                onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
                onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
              >
                <img src={signatureImage} alt="Signature" style={{ height: '80px', display: 'block' }} />
                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
                  Authorized Signatory
                </div>
              </div>
            ) : (
              <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '220px', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#eef2ff',
        borderRadius: '6px',
        textAlign: 'center'
      }}>
        <span style={{
          fontSize: '13px',
          fontStyle: 'italic',
          color: '#4f46e5',
          fontWeight: '600'
        }}>
          Amount in Words: {totalInWords}
        </span>
      </div>
    </div>
  );

  // return (
  //   <div style={{
  //     fontFamily: 'Helvetica, sans-serif',
  //     padding: '40px',
  //     backgroundColor: '#fff',
  //     minHeight: '400px',
  //     position: 'relative'
  //   }}>



  //     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
  //       <div style={{ borderLeft: '4px solid #14367eff', paddingLeft: '20px' }}>
  //         <h1 style={{
  //           margin: '0',
  //           fontSize: `${fonts.invoiceTitle.size}px`,
  //           fontFamily: fonts.invoiceTitle.family,
  //           color: fonts.invoiceTitle.color,
  //           fontWeight: fonts.invoiceTitle.fontWeight // FIX: Use merged fontWeight
  //         }}>
  //           {data.invoice?.documentType || 'INVOICE'}
  //         </h1>
  //         <p style={{ margin: '5px 0 0 0', fontSize: '16px', color: '#64748b' }}>{data.invoice?.number}</p>
  //         <h1 style={{
  //           margin: '0 0 5px 0',
  //           fontSize: `${fonts.companyName.size}px`,
  //           fontFamily: fonts.companyName.family,
  //           color: fonts.companyName.color,
  //           fontWeight: fonts.companyName.fontWeight // FIX: Use merged fontWeight
  //         }}>
  //           {data.billFrom?.name}
  //         </h1>
  //         <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.address}</p>
  //       </div>

  //       <div style={{ textAlign: 'right', minHeight: '100px' }}>
  //         {data.company_logo && (
  //           <div
  //             style={{
  //               position: 'absolute',
  //               left: `${logoPosition.x}px`,
  //               top: `${logoPosition.y}px`,
  //               cursor: 'move',
  //               zIndex: 100,
  //               border: '2px dashed transparent',
  //               transition: 'border 0.2s'
  //             }}
  //             draggable
  //             onDragEnd={onLogoDrag}
  //             onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #94a3b8'}
  //             onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //           >
  //             <img
  //               src={data.company_logo}
  //               alt="Company Logo"
  //               style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
  //             />
  //           </div>
  //         )}
  //         {/* {data.company_logo && (
  //           <div
  //             style={{
  //               position: 'absolute',
  //               // Perfect top-right placement (A4-safe, clean spacing)
  //               right: '40px',
  //               top: '40px',
  //               cursor: 'move',
  //               zIndex: 100,
  //               padding: '8px',
  //               backgroundColor: 'rgba(255, 255, 255, 0.95)',
  //               borderRadius: '12px',
  //               boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  //               transition: 'all 0.2s ease',
  //               // Visual drag feedback
  //               border: '2px dashed transparent',
  //             }}
  //             draggable
  //             onDragEnd={onLogoDrag}
  //             onMouseOver={(e) => {
  //               e.currentTarget.style.border = '2px dashed #3b82f6';
  //               e.currentTarget.style.transform = 'translateY(-2px)';
  //               e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15)';
  //             }}
  //             onMouseOut={(e) => {
  //               e.currentTarget.style.border = '2px dashed transparent';
  //               e.currentTarget.style.transform = 'translateY(0)';
  //               e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
  //             }}
  //           >
  //             <img
  //               src={data.company_logo}
  //               alt="Company Logo"
  //               style={{
  //                 width: `${logoSize}px`,
  //                 maxWidth: '160px',
  //                 height: 'auto',
  //                 objectFit: 'contain',
  //                 display: 'block',
  //                 borderRadius: '8px',
  //               }}
  //             />
  //           </div>
  //         )} */}
  //       </div>
  //     </div>

  //     {data.dispatchFrom && (
  //       <div style={{
  //         marginBottom: '20px',
  //         padding: '15px',
  //         backgroundColor: '#f8fafc',
  //         borderRadius: '8px',
  //         borderLeft: '3px solid #2563eb'
  //       }}>
  //         <h3 style={{
  //           margin: '0 0 8px 0',
  //           fontSize: `${fonts.sectionHeaders.size}px`,
  //           fontFamily: fonts.sectionHeaders.family,
  //           color: fonts.sectionHeaders.color,
  //           fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
  //           letterSpacing: '1px'
  //         }}>
  //           DISPATCH FROM
  //         </h3>
  //         <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.dispatchFrom.name}</p>
  //         <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.dispatchFrom.address}</p>
  //       </div>
  //     )}

  //     <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
  //       <div>
  //         <div style={{ marginBottom: '20px' }}>
  //           <h3 style={{
  //             margin: '0 0 8px 0',
  //             fontSize: `${fonts.sectionHeaders.size}px`,
  //             fontFamily: fonts.sectionHeaders.family,
  //             color: fonts.sectionHeaders.color,
  //             fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
  //             letterSpacing: '1px'
  //           }}>FROM</h3>
  //           <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.billFrom?.name}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.address}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.state}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billFrom?.pincode}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billFrom?.gstin}</p>
  //         </div>
  //         <div style={{ marginBottom: '20px' }}>
  //           <h3 style={{
  //             margin: '0 0 8px 0',
  //             fontSize: `${fonts.sectionHeaders.size}px`,
  //             fontFamily: fonts.sectionHeaders.family,
  //             color: fonts.sectionHeaders.color,
  //             fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
  //             letterSpacing: '1px'
  //           }}>BILL TO</h3>
  //           <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.billTo?.name}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>{data.billTo?.address}</p>
  //           <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
  //         </div>
  //         {data.shipTo && (
  //           <div>
  //             <h3 style={{
  //               margin: '0 0 8px 0',
  //               fontSize: `${fonts.sectionHeaders.size}px`,
  //               fontFamily: fonts.sectionHeaders.family,
  //               color: fonts.sectionHeaders.color,
  //               fontWeight: fonts.sectionHeaders.fontWeight, // FIX: Use merged fontWeight
  //               letterSpacing: '1px'
  //             }}>SHIP TO</h3>
  //             <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
  //               {data.shipTo?.name}
  //             </p>
  //             <p style={{ margin: '2px 0', fontSize: '13px', color: '#64748b' }}>
  //               {data.shipTo?.address}
  //             </p>
  //           </div>
  //         )}
  //       </div>
  //       <div style={{ textAlign: 'right' }}>
  //         <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
  //           {data.invoice?.documentType === "Invoice" && (
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
  //               <span style={{ fontWeight: '600' }}>Invoice Date:</span> {data.invoice?.date}
  //             </p>
  //           )}
  //           {data.invoice?.documentType === "Bill" && (
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
  //               <span style={{ fontWeight: '600' }}>Bill Date:</span> {data.invoice?.date}
  //             </p>
  //           )}

  //           {data.invoice?.originalDate && data.invoice.originalDate !== 'null' && (
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
  //               <span style={{ fontWeight: '600' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
  //             </p>
  //           )}
  //           {data.invoice?.originalNumber && data.invoice.originalNumber !== 'null' && (
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
  //               <span style={{ fontWeight: '600' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
  //             </p>
  //           )}
  //           <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
  //             <span style={{ fontWeight: '600' }}>Place of Supply:</span> {data.placeOfSupply}
  //           </p>
  //           {data.invoice?.reverseCharge === 'Yes' && (
  //             <p style={{ margin: '8px 0 4px 0', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
  //               Reverse Charge Applicable
  //             </p>
  //           )}
  //         </div>
  //       </div>
  //     </div>

  //     <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
  //       <colgroup>
  //         {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
  //         {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
  //         {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
  //         {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
  //         {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
  //         {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
  //         {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
  //         {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
  //       </colgroup>
  //       <thead>
  //         <tr style={{ backgroundColor: '#f5f5f5' }}>
  //           {columnVisibility.srNo && (
  //             <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.item && (
  //             <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.hsn && (
  //             <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.qty && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.rate && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.gst && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.discount && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
  //             </th>
  //           )}
  //           {columnVisibility.amount && (
  //             <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
  //               <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
  //             </th>
  //           )}
  //         </tr>
  //       </thead>
  //       <tbody>
  //         {data.items?.map((item, index) => (
  //           <tr key={item.id}>
  //             {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
  //             {columnVisibility.item && (
  //               <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //                 <div style={{ fontWeight: 'bold' }}>{item.name}</div>
  //                 {item.description && <div style={{ fontSize: '11px', color: '#666' }}>{item.description}</div>}
  //               </td>
  //             )}
  //             {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
  //             {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
  //             {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.rate)}
  //             </td>}
  //             {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
  //             {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.discount)}
  //             </td>}
  //             {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
  //               {formatCurrency(item.amount)}
  //             </td>}
  //           </tr>
  //         ))}
  //       </tbody>
  //     </table>

  //     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
  //       <div style={{ flex: 1 }}>
  //         <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px', maxWidth: '400px' }}>
  //           <h3 style={{ margin: '0 0 12px 0', fontSize: '11px', fontWeight: '600', color: '#94a3b8', letterSpacing: '1px' }}> BANK DETAILS </h3>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
  //             <span style={{ fontSize: '12px', color: '#64748b' }}>Bank:</span>
  //             <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.bankName}</span>
  //           </div>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
  //             <span style={{ fontSize: '12px', color: '#64748b' }}>Account Holder:</span>
  //             <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.accountholdername}</span>
  //           </div>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
  //             <span style={{ fontSize: '12px', color: '#64748b' }}>Account Number:</span>
  //             <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.accountNumber}</span>
  //           </div>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
  //             <span style={{ fontSize: '12px', color: '#64748b' }}>IFSC Code:</span>
  //             <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{data.bankInfo?.ifscCode}</span>
  //           </div>
  //         </div>
  //       </div>

  //       {/* RIGHT COLUMN: Totals + Signature (Properly aligned) */}
  //       <div style={{ minWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
  //         {/* Totals Box */}
  //         <div style={{ width: '100%', maxWidth: '350px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '60px' }}>
  //           <div style={{ display: 'flex', flexDirection: 'column' }}>
  //             <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
  //             </div>
  //             {data.cgst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
  //               </div>
  //             )}
  //             {data.sgst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
  //               </div>
  //             )}
  //             {data.igst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
  //               </div>
  //             )}
  //             {data.otherCharges > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>Other Charges</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.otherCharges)}</span>
  //               </div>
  //             )}
  //             {data.roundOff != null && data.roundOff !== 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>{formatCurrency(data.roundOff)}</span>
  //               </div>
  //             )}
  //             <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.discount)}</span>
  //             </div>
  //             <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #2563eb', marginTop: '12px' }}>
  //               <span style={{
  //                 fontSize: `${fonts.totals.size}px`,
  //                 fontFamily: fonts.totals.family,
  //                 color: fonts.totals.color,
  //                 fontWeight: fonts.totals.fontWeight
  //               }}>Total</span>
  //               <span style={{
  //                 fontSize: `${fonts.totals.size}px`,
  //                 fontFamily: fonts.totals.family,
  //                 color: fonts.totals.color,
  //                 fontWeight: fonts.totals.fontWeight
  //               }}>{formatCurrency(data.total)}</span>
  //             </div>
  //           </div>
  //         </div>

  //         {/* Authorized Signature - Now clearly visible at bottom right */}
  //         <div style={{ width: '100%', textAlign: 'right', marginTop: 'auto' }}>
  //           {signatureImage ? (
  //             <div
  //               style={{
  //                 position: 'relative',
  //                 display: 'inline-block',
  //                 cursor: 'move',
  //                 border: '2px dashed transparent',
  //                 transition: 'border 0.2s'
  //               }}
  //               draggable
  //               onDragEnd={onSignatureDrag}
  //               onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
  //               onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //             >
  //               <img src={signatureImage} alt="Signature" style={{ height: '80px', display: 'block' }} />
  //               <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
  //                 Authorized Signatory
  //               </div>
  //             </div>
  //           ) : (
  //             <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '220px', textAlign: 'center' }}>
  //               <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
  //             </div>
  //           )}
  //         </div>
  //       </div>
  //     </div>

  //     <div style={{
  //       marginTop: '16px',
  //       padding: '12px',
  //       backgroundColor: '#eef2ff',
  //       borderRadius: '6px',
  //       textAlign: 'center'
  //     }}>
  //       <span style={{
  //         fontSize: '13px',
  //         fontStyle: 'italic',
  //         color: '#4f46e5',
  //         fontWeight: '600'
  //       }}>
  //         Amount in Words: {totalInWords}
  //       </span>
  //     </div>
  //   </div>
  // );
};

const BoldTemplate = ({
  data,
  columnVisibility = { srNo: true, item: true, hsn: true, qty: true, rate: true, gst: true, discount: true, amount: true },
  headerLabels = { item: 'DESCRIPTION', hsn: 'HSN/SAC', qty: 'QTY', rate: 'RATE', gst: 'GST %', discount: 'DISCOUNT', amount: 'AMOUNT' },
  logoSize = 100,
  logoPosition = { x: 30, y: 30 },
  signatureImage,
  signaturePosition = { x: 500, y: 900 },
  onLogoDrag,
  onSignatureDrag,
  fontSettings = {},
  columnWidths = {},
  onColumnResize,
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  // Default font settings
  const defaultFontSettings = {
    companyName: { size: 30, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
    invoiceTitle: { size: 18, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
    sectionHeaders: { size: 14, family: 'Arial, sans-serif', color: '#1e40af', fontWeight: '700' },
    tableHeaders: { size: 12, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
    tableContent: { size: 14, family: 'Arial, sans-serif', color: '#1e293b', fontWeight: 'normal' },
    totals: { size: 18, family: 'Arial, sans-serif', color: '#1e293b', fontWeight: '700' }
  };

  const fonts = { ...defaultFontSettings, ...fontSettings }; // Simple merge (or use your mergeFonts if available)

  // Number to Words
  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
    };

    let str = '';
    num = Math.round(num * 100) / 100;
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);

    if (rupees > 0) {
      if (rupees >= 10000000) str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
      if (rupees % 10000000 >= 100000) str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
      if (rupees % 100000 >= 1000) str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
      str += convertBelowThousand(rupees % 1000);
      str += ' Rupees';
    }
    if (paise > 0) {
      str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
    }
    return str.trim() + ' Only';
  };

  const totalInWords = numberToWords(parseFloat(data.total || 0));

  // Robust Currency Formatter
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '₹0.00';

    const num = parseFloat(String(value).replace(/,/g, ''));

    if (isNaN(num)) return '₹0.00';

    return '₹' + num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const f = (key) => ({
    fontSize: `${fonts[key].size}px`,
    fontFamily: fonts[key].family,
    color: fonts[key].color,
    fontWeight: fonts[key].fontWeight,
  });


  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      padding: '0',
      backgroundColor: '#fff',
      minHeight: '400px',
      position: 'relative'
    }}>
      {/* Header with Gradient */}
      <div style={{
        background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)',
        color: '#fff',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <div style={{ position: 'relative', padding: '40px 30px', minHeight: '120px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '40px' }}>
            <div style={{ flex: 1, color: '#ffffff' }}>
              <h1 style={{ margin: '0 0 8px 0', ...f('companyName'), lineHeight: '1.1' }}>
                {data.billFrom?.name}
              </h1>
              <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.address}</p>
              <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.state}</p>
              <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.pincode}</p>
              <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>GSTIN: {data.billFrom?.gstin}</p>
              <h2 style={{ margin: '20px 0 4px 0', ...f('invoiceTitle'), textTransform: 'uppercase', letterSpacing: '1px' }}>
                {data.invoice?.documentType || 'INVOICE'}
              </h2>
              <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#ffffff' }}>
                {data.invoice?.number}
              </p>
            </div>
            <div style={{ flexShrink: 0, width: 'auto', textAlign: 'right' }}>
              {data.company_logo && (
                <div style={{ display: 'inline-block', padding: '4px' }}>
                  <img src={data.company_logo} alt="Company Logo" style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 30px' }}>
        {/* Bill To / Ship To / Invoice Details */}
        <div style={{ padding: '0 30px', marginBottom: '30px' }}>
          {data.dispatchFrom && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 10px 0', ...f('sectionHeaders'), color: '#1e40af' }}>DISPATCH FROM</h3>
              <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.dispatchFrom?.name}</p>
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.dispatchFrom?.address}</p>
              {data.dispatchFrom?.gstin && <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.dispatchFrom.gstin}</p>}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px', marginBottom: '20px' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h3 style={{ margin: '0 0 12px 0', ...f('sectionHeaders') }}>BILL TO</h3>
              <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{data.billTo?.name}</p>
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.billTo?.address}</p>
              <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
            </div>
            {data.shipTo && (
              <div style={{ flex: 1, minWidth: '300px' }}>
                <h3 style={{ margin: '0 0 12px 0', ...f('sectionHeaders') }}>SHIP TO</h3>
                <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{data.shipTo?.name}</p>
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.shipTo?.address}</p>
              </div>
            )}
          </div>

          <div style={{ padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
              {data.invoice?.documentType === "Invoice" && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
                  <span style={{ fontWeight: '600' }}>Invoice Date:</span> {data.invoice?.date}
                </p>
              )}
              {data.invoice?.documentType === "Bill" && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
                  <span style={{ fontWeight: '600' }}>Bill Date:</span> {data.invoice?.date}
                </p>
              )}
              {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
                  <span style={{ fontWeight: '600' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
                </p>
              )}
              {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
                <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
                  <span style={{ fontWeight: '600' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
                </p>
              )}
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
                <span style={{ fontWeight: '600' }}>Place of Supply:</span> {data.placeOfSupply}
              </p>
            </div>
            {data.invoice?.reverseCharge === 'Yes' && (
              <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#dc2626', fontWeight: '700' }}>
                Reverse Charge Applicable
              </p>
            )}
          </div>
        </div>

        {/* Items Table */}
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
          <colgroup>
            {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
            {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
            {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
            {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
            {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
            {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
            {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
            {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
          </colgroup>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)' }}>
              {columnVisibility.srNo && <th style={{ padding: '10px', textAlign: 'left', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader></th>}
              {columnVisibility.item && <th style={{ padding: '10px', textAlign: 'left', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader></th>}
              {columnVisibility.hsn && <th style={{ padding: '10px', textAlign: 'center', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader></th>}
              {columnVisibility.qty && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader></th>}
              {columnVisibility.rate && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader></th>}
              {columnVisibility.gst && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader></th>}
              {columnVisibility.discount && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader></th>}
              {columnVisibility.amount && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader></th>}
            </tr>
          </thead>
          <tbody>
            {data.items?.map((item, index) => (
              <tr key={item.id}>
                {columnVisibility.srNo && <td style={{ padding: '12px 10px', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
                {columnVisibility.item && (
                  <td style={{ padding: '12px 10px', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
                    <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                    {item.line_item_description && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{item.line_item_description}</div>}
                  </td>
                )}
                {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
                {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
                {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{formatCurrency(item.rate)}</td>}
                {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
                {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
                  {item.discount > 0 ? formatCurrency(item.discount) : '-'}
                </td>}
                {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
                  {formatCurrency(item.amount)}
                </td>}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals & Bank Info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
          <div style={{ width: '48%', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 10px 0', ...f('sectionHeaders'), textTransform: 'uppercase' }}>Bank Details</h4>
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>Bank:</span> {data.bankInfo?.bankName}</p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c Name:</span> {data.bankInfo?.accountholdername}</p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c No:</span> {data.bankInfo?.accountNumber}</p>
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>IFSC:</span> {data.bankInfo?.ifscCode}</p>
          </div>

          <div style={{ width: '48%', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ minWidth: '300px', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
              </div>
              {data.cgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
                </div>
              )}
              {data.sgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
                </div>
              )}
              {data.igst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                  {data.discount > 0 ? formatCurrency(data.discount) : '₹0.00'}
                </span>
              </div>
              {data.roundOff != null && data.roundOff !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>
                    {formatCurrency(data.roundOff)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #3b82f6', marginTop: '12px' }}>
                <span style={f('totals')}>TOTAL</span>
                <span style={f('totals')}>{formatCurrency(data.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Amount in Words */}
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', textAlign: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', fontStyle: 'italic' }}>
            Amount in Words: {totalInWords}
          </span>
        </div>

        {/* Signature */}
        <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
          {signatureImage ? (
            <div style={{ position: 'relative', left: `${signaturePosition.x}px`, cursor: 'move', display: 'inline-block', border: '2px dashed transparent', transition: 'border 0.2s' }}
              draggable onDragEnd={onSignatureDrag}
              onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #2563eb'}
              onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
            >
              <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
              <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
                Authorized Signatory
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // return (
  //   <div style={{
  //     fontFamily: 'Arial, sans-serif',
  //     padding: '0',
  //     backgroundColor: '#fff',
  //     minHeight: '400px',
  //     position: 'relative'
  //   }}>
  //     {/* Header with Gradient */}
  //     <div style={{
  //       background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)',
  //       color: '#fff',
  //       padding: '30px',
  //       marginBottom: '30px'
  //     }}>
  //       <div style={{ position: 'relative', padding: '40px 30px', minHeight: '120px' }}>
  //         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '40px' }}>
  //           <div style={{ flex: 1, color: '#ffffff' }}>
  //             <h1 style={{ margin: '0 0 8px 0', ...f('companyName'), lineHeight: '1.1' }}>
  //               {data.billFrom?.name}
  //             </h1>
  //             <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.address}</p>
  //             <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.state}</p>
  //             <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>{data.billFrom?.pincode}</p>
  //             <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>GSTIN: {data.billFrom?.gstin}</p>
  //             <h2 style={{ margin: '20px 0 4px 0', ...f('invoiceTitle'), textTransform: 'uppercase', letterSpacing: '1px' }}>
  //               {data.invoice?.documentType || 'INVOICE'}
  //             </h2>
  //             <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#ffffff' }}>
  //               {data.invoice?.number}
  //             </p>
  //           </div>
  //           <div style={{ flexShrink: 0, width: 'auto', textAlign: 'right' }}>
  //             {data.company_logo && (
  //               <div
  //                 style={{ display: 'inline-block', cursor: 'move', border: '2px dashed transparent', transition: 'border 0.2s', padding: '4px' }}
  //                 draggable
  //                 onDragEnd={onLogoDrag}
  //                 onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
  //                 onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //               >
  //                 <img src={data.company_logo} alt="Company Logo" style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }} />
  //               </div>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     <div style={{ padding: '0 30px' }}>
  //       {/* Bill To / Ship To / Invoice Details */}
  //       <div style={{ padding: '0 30px', marginBottom: '30px' }}>
  //         {data.dispatchFrom && (
  //           <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
  //             <h3 style={{ margin: '0 0 10px 0', ...f('sectionHeaders'), color: '#1e40af' }}>DISPATCH FROM</h3>
  //             <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{data.dispatchFrom?.name}</p>
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.dispatchFrom?.address}</p>
  //             {data.dispatchFrom?.gstin && <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.dispatchFrom.gstin}</p>}
  //           </div>
  //         )}

  //         <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px', marginBottom: '20px' }}>
  //           <div style={{ flex: 1, minWidth: '300px' }}>
  //             <h3 style={{ margin: '0 0 12px 0', ...f('sectionHeaders') }}>BILL TO</h3>
  //             <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{data.billTo?.name}</p>
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.billTo?.address}</p>
  //             <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
  //           </div>
  //           {data.shipTo && (
  //             <div style={{ flex: 1, minWidth: '300px' }}>
  //               <h3 style={{ margin: '0 0 12px 0', ...f('sectionHeaders') }}>SHIP TO</h3>
  //               <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>{data.shipTo?.name}</p>
  //               <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>{data.shipTo?.address}</p>
  //             </div>
  //           )}
  //         </div>

  //         <div style={{ padding: '15px', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
  //           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
  //             {data.invoice?.documentType === "Invoice" && (
  //               <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
  //                 <span style={{ fontWeight: '600' }}>Invoice Date:</span> {data.invoice?.date}
  //               </p>
  //             )}
  //             {data.invoice?.documentType === "Bill" && (
  //               <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
  //                 <span style={{ fontWeight: '600' }}>Bill Date:</span> {data.invoice?.date}
  //               </p>
  //             )}
  //             {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
  //               <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
  //                 <span style={{ fontWeight: '600' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
  //               </p>
  //             )}
  //             {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
  //               <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
  //                 <span style={{ fontWeight: '600' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
  //               </p>
  //             )}
  //             <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
  //               <span style={{ fontWeight: '600' }}>Place of Supply:</span> {data.placeOfSupply}
  //             </p>
  //           </div>
  //           {data.invoice?.reverseCharge === 'Yes' && (
  //             <p style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#dc2626', fontWeight: '700' }}>
  //               Reverse Charge Applicable
  //             </p>
  //           )}
  //         </div>
  //       </div>

  //       {/* Items Table */}
  //       <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
  //         <colgroup>
  //           {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
  //           {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
  //           {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
  //           {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
  //           {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
  //           {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
  //           {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
  //           {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
  //         </colgroup>
  //         <thead>
  //           <tr style={{ background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)' }}>
  //             {columnVisibility.srNo && <th style={{ padding: '10px', textAlign: 'left', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader></th>}
  //             {columnVisibility.item && <th style={{ padding: '10px', textAlign: 'left', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader></th>}
  //             {columnVisibility.hsn && <th style={{ padding: '10px', textAlign: 'center', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader></th>}
  //             {columnVisibility.qty && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader></th>}
  //             {columnVisibility.rate && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader></th>}
  //             {columnVisibility.gst && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader></th>}
  //             {columnVisibility.discount && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader></th>}
  //             {columnVisibility.amount && <th style={{ padding: '10px', textAlign: 'right', ...f('tableHeaders'), borderBottom: '2px solid #ddd' }}><ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader></th>}
  //           </tr>
  //         </thead>
  //         <tbody>
  //           {data.items?.map((item, index) => (
  //             <tr key={item.id}>
  //               {columnVisibility.srNo && <td style={{ padding: '12px 10px', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
  //               {columnVisibility.item && (
  //                 <td style={{ padding: '12px 10px', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
  //                   <div style={{ fontWeight: 'bold' }}>{item.name}</div>
  //                   {item.description && <div style={{ fontSize: '11px', color: '#666' }}>{item.description}</div>}
  //                 </td>
  //               )}
  //               {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
  //               {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
  //               {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{formatCurrency(item.rate)}</td>}
  //               {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
  //               {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
  //                 {item.discount > 0 ? formatCurrency(item.discount) : '-'}
  //               </td>}
  //               {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', ...f('tableContent'), borderBottom: '1px solid #e0e0e0' }}>
  //                 {formatCurrency(item.amount)}
  //               </td>}
  //             </tr>
  //           ))}
  //         </tbody>
  //       </table>

  //       {/* Totals & Bank Info */}
  //       <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
  //         <div style={{ width: '48%', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px' }}>
  //           <h4 style={{ margin: '0 0 10px 0', ...f('sectionHeaders'), textTransform: 'uppercase' }}>Bank Details</h4>
  //           <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>Bank:</span> {data.bankInfo?.bankName}</p>
  //           <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c Name:</span> {data.bankInfo?.accountholdername}</p>
  //           <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c No:</span> {data.bankInfo?.accountNumber}</p>
  //           <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>IFSC:</span> {data.bankInfo?.ifscCode}</p>
  //         </div>

  //         <div style={{ width: '48%', display: 'flex', justifyContent: 'flex-end' }}>
  //           <div style={{ minWidth: '300px', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px' }}>
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
  //             </div>
  //             {data.cgst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
  //               </div>
  //             )}
  //             {data.sgst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
  //               </div>
  //             )}
  //             {data.igst > 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
  //               </div>
  //             )}
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
  //                 {data.discount > 0 ? formatCurrency(data.discount) : '₹0.00'}
  //               </span>
  //             </div>
  //             {data.roundOff != null && data.roundOff !== 0 && (
  //               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
  //                 <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
  //                 <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>
  //                   {formatCurrency(data.roundOff)}
  //                 </span>
  //               </div>
  //             )}
  //             <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #3b82f6', marginTop: '12px' }}>
  //               <span style={f('totals')}>TOTAL</span>
  //               <span style={f('totals')}>{formatCurrency(data.total)}</span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>

  //       {/* Amount in Words */}
  //       <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', textAlign: 'center' }}>
  //         <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', fontStyle: 'italic' }}>
  //           Amount in Words: {totalInWords}
  //         </span>
  //       </div>

  //       {/* Signature */}
  //       <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
  //         {signatureImage ? (
  //           <div style={{ position: 'relative', left: `${signaturePosition.x}px`, cursor: 'move', display: 'inline-block', border: '2px dashed transparent', transition: 'border 0.2s' }}
  //             draggable onDragEnd={onSignatureDrag}
  //             onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #2563eb'}
  //             onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //           >
  //             <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
  //             <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
  //               Authorized Signatory
  //             </div>
  //           </div>
  //         ) : (
  //           <div style={{ textAlign: 'right' }}>
  //             <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
  //               <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
  //             </div>
  //           </div>
  //         )}
  //       </div>
  //     </div>
  //   </div>
  // );
};



// const BoldTemplate = ({
//   data,
//   columnVisibility = { srNo: true, item: true, hsn: true, qty: true, rate: true, gst: true, discount: true, amount: true },
//   headerLabels = { item: 'DESCRIPTION', hsn: 'HSN/SAC', qty: 'QTY', rate: 'RATE', gst: 'GST %', discount: 'DISCOUNT', amount: 'AMOUNT' },
//   logoSize = 100,
//   logoPosition = { x: 30, y: 30 },
//   signatureImage,
//   signaturePosition = { x: 500, y: 900 },
//   onLogoDrag,
//   onSignatureDrag,
//   fontSettings = {},
//   columnWidths = {},
//   onColumnResize,
// }) => {
//   const formatDate = (dateStr) => {
//     if (!dateStr) return '';
//     return new Date(dateStr).toLocaleDateString('en-GB');
//   };

//   // Default font settings using Classic keys
//   const defaultFontSettings = {
//     companyName: { size: 30, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
//     invoiceTitle: { size: 18, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
//     sectionHeaders: { size: 14, family: 'Arial, sans-serif', color: '#1e40af', fontWeight: '700' },
//     tableHeaders: { size: 12, family: 'Arial, sans-serif', color: '#ffffff', fontWeight: '700' },
//     tableContent: { size: 14, family: 'Arial, sans-serif', color: '#1e293b', fontWeight: 'normal' },
//     totals: { size: 18, family: 'Arial, sans-serif', color: '#1e293b', fontWeight: '700' }
//   };

//   // Use robust font merging
//   const fonts = mergeFonts(defaultFontSettings, fontSettings);

//   const numberToWords = (num) => {
//     if (num === 0) return 'Zero Rupees Only';

//     const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
//       'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
//     const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

//     const convertBelowThousand = (n) => {
//       if (n === 0) return '';
//       if (n < 20) return ones[n];
//       if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
//       return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
//     };

//     let str = '';
//     num = Math.round(num * 100) / 100;
//     const rupees = Math.floor(num);
//     const paise = Math.round((num - rupees) * 100);

//     if (rupees > 0) {
//       if (rupees >= 10000000) {
//         str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
//       }
//       if (rupees % 10000000 >= 100000) {
//         str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
//       }
//       if (rupees % 100000 >= 1000) {
//         str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
//       }
//       str += convertBelowThousand(rupees % 1000);
//       str += ' Rupees';
//     }

//     if (paise > 0) {
//       str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
//     }

//     return str.trim() + ' Only';
//   };

//   const totalInWords = numberToWords(parseFloat(data.total || 0));


// const formatCurrency = (value) => {
//   if (value === null || value === undefined || value === '') return '₹0.00';

//   // Remove ₹, commas, spaces, everything except digits, dot, minus
//   const cleaned = String(value).replace(/[^0-9.-]/g, '');

//   const num = parseFloat(cleaned);

//   if (isNaN(num)) return '₹0.00';

//   return '₹' + num.toLocaleString('en-IN', {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2
//   });
// };

//   const f = (key) => ({
//     fontSize: `${fonts[key].size}px`,
//     fontFamily: fonts[key].family,
//     color: fonts[key].color,
//     fontWeight: fonts[key].fontWeight,
//   });

//   // Enhanced Premium Blue Theme Colors
//   const primaryGradient = 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 45%, #60a5fa 100%)';
//   const accentColor = '#3b82f6';
//   const accentHover = '#2563eb';
//   const accentLight = '#93c5fd';
//   const logoHoverDash = '#fbbf24';

//   return (
//     <div style={{
//       fontFamily: 'Arial, sans-serif',
//       padding: '0',
//       backgroundColor: '#fff',
//       minHeight: '400px',
//       position: 'relative'
//     }}>

//       <div style={{
//         background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)',
//         color: '#fff',
//         padding: '30px',
//         marginBottom: '30px'
//       }}>
//         {/* HEADER - Perfectly Aligned with Logo on Right */}
//         <div style={{
//           position: 'relative',
//           // backgroundColor: '#1e293b',
//           padding: '40px 30px',
//           minHeight: '120px'  // gives space for logo
//         }}>
//           <div style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'flex-start',   // ← important: align to top
//             gap: '40px'                 // extra breathing room
//           }}>
//             {/* Left Side: Company & Invoice Info */}
//             <div style={{ flex: 1, color: '#ffffff' }}>
//               <h1 style={{
//                 margin: '0 0 8px 0',
//                 ...f('companyName'),
//                 lineHeight: '1.1'
//               }}>
//                 {data.billFrom?.name}
//               </h1>
//               <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>
//                 {data.billFrom?.address}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>
//                 {data.billFrom?.state}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>
//                 {data.billFrom?.pincode}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '14px', opacity: 0.9 }}>
//                 GSTIN: {data.billFrom?.gstin}
//               </p>

//               <h2 style={{
//                 margin: '20px 0 4px 0',
//                 ...f('invoiceTitle'),
//                 textTransform: 'uppercase',
//                 letterSpacing: '1px'
//               }}>
//                 {data.invoice?.documentType || 'INVOICE'}
//               </h2>
//               <p style={{
//                 margin: '0',
//                 fontSize: '18px',
//                 fontWeight: 'bold',
//                 color: '#ffffff'
//               }}>
//                 {data.invoice?.number}
//               </p>
//             </div>

//             {/* Right Side: Logo (Always Top-Right) */}
//             <div style={{
//               flexShrink: 0,
//               width: 'auto',
//               textAlign: 'right'
//             }}>
//               {data.company_logo && (
//                 <div
//                   style={{
//                     display: 'inline-block',
//                     cursor: 'move',
//                     border: '2px dashed transparent',
//                     transition: 'border 0.2s',
//                     padding: '4px'
//                   }}
//                   draggable
//                   onDragEnd={onLogoDrag}
//                   onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
//                   onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
//                 >
//                   <img
//                     src={data.company_logo}
//                     alt="Company Logo"
//                     style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
//                   />
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div style={{ padding: '0 30px' }}>
//         {/* === NEW: Dispatch From, Bill To, Ship To, and Invoice Details Section === */}
//         <div style={{ padding: '0 30px', marginBottom: '30px' }}>
//           {/* Dispatch From - Optional, shown only if provided */}
//           {data.dispatchFrom && (
//             <div style={{
//               marginBottom: '20px',
//               padding: '15px',
//               backgroundColor: '#f0f9ff',
//               border: '1px solid #bfdbfe',
//               borderRadius: '8px'
//             }}>
//               <h3 style={{
//                 margin: '0 0 10px 0',
//                 ...f('sectionHeaders'),
//                 color: '#1e40af'
//               }}>DISPATCH FROM</h3>
//               <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
//                 {data.dispatchFrom?.name}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
//                 {data.dispatchFrom?.address}
//               </p>
//               {data.dispatchFrom?.gstin && (
//                 <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
//                   GSTIN: {data.dispatchFrom.gstin}
//                 </p>
//               )}
//             </div>
//           )}

//           {/* Bill To and Ship To side by side */}
//           <div style={{ display: 'flex', justifyContent: 'space-between', gap: '30px', marginBottom: '20px' }}>
//             {/* Bill To - Always shown */}
//             <div style={{ flex: 1, minWidth: '300px' }}>
//               <h3 style={{
//                 margin: '0 0 12px 0',
//                 ...f('sectionHeaders')
//               }}>BILL TO</h3>
//               <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
//                 {data.billTo?.name}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
//                 {data.billTo?.address}
//               </p>
//               <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
//                 GSTIN: {data.billTo?.gstin}
//               </p>
//             </div>

//             {/* Ship To - Only if provided or different from Bill To */}
//             {data.shipTo && (
//               <div style={{ flex: 1, minWidth: '300px' }}>
//                 <h3 style={{
//                   margin: '0 0 12px 0',
//                   ...f('sectionHeaders')
//                 }}>SHIP TO</h3>
//                 <p style={{ margin: '4px 0', fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
//                   {data.shipTo?.name}
//                 </p>
//                 <p style={{ margin: '4px 0', fontSize: '13px', color: '#64748b' }}>
//                   {data.shipTo?.address}
//                 </p>

//               </div>
//             )}
//           </div>

//           {/* Invoice Details Box (Date, Place of Supply, etc.) */}
//           <div style={{
//             padding: '15px',
//             backgroundColor: '#f0f9ff',
//             border: '1px solid #bfdbfe',
//             borderRadius: '8px'
//           }}>
//             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
//               {data.invoice?.documentType === "Invoice" && (
//                 <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
//                   <span style={{ fontWeight: '600' }}>Invoice Date:</span> {(data.invoice?.date)}
//                 </p>
//               )}
//               {data.invoice?.documentType === "Bill" && (
//                 <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
//                   <span style={{ fontWeight: '600' }}>Bill Date:</span> {(data.invoice?.date)}
//                 </p>
//               )}
//               {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
//                 <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
//                   <span style={{ fontWeight: '600' }}>Original Invoice Date:</span> {data.invoice?.originalDate}
//                 </p>
//               )}
//               {data.invoice?.originalNumber && data.invoice?.originalNumber !== 'null' && (
//                 <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
//                   <span style={{ fontWeight: '600' }}>Original Invoice Number:</span> {data.invoice?.originalNumber}
//                 </p>
//               )}
//               <p style={{ margin: '4px 0', fontSize: '14px', color: '#1e293b' }}>
//                 <span style={{ fontWeight: '600' }}>Place of Supply:</span> {data.placeOfSupply}
//               </p>
//             </div>
//             {data.invoice?.reverseCharge === 'Yes' && (
//               <p style={{
//                 margin: '12px 0 0 0',
//                 fontSize: '14px',
//                 color: '#dc2626',
//                 fontWeight: '700'
//               }}>
//                 Reverse Charge Applicable
//               </p>
//             )}
//           </div>
//         </div>
//         {/* === End of New Section === */}

//         <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginBottom: '20px' }}>
//           <colgroup>
//             {columnVisibility.srNo && <col style={{ width: columnWidths.srNo || '50px' }} />}
//             {columnVisibility.item && <col style={{ width: columnWidths.item || '280px' }} />}
//             {columnVisibility.hsn && <col style={{ width: columnWidths.hsn || '90px' }} />}
//             {columnVisibility.qty && <col style={{ width: columnWidths.qty || '80px' }} />}
//             {columnVisibility.rate && <col style={{ width: columnWidths.rate || '100px' }} />}
//             {columnVisibility.gst && <col style={{ width: columnWidths.gst || '80px' }} />}
//             {columnVisibility.discount && <col style={{ width: columnWidths.discount || '100px' }} />}
//             {columnVisibility.amount && <col style={{ width: columnWidths.amount || '120px' }} />}
//           </colgroup>

//           <thead>
//             <tr style={{ background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)' }}>
//               {columnVisibility.srNo && (
//                 <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.item && (
//                 <th style={{ padding: '10px', textAlign: 'left', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.hsn && (
//                 <th style={{ padding: '10px', textAlign: 'center', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.qty && (
//                 <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.rate && (
//                 <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.gst && (
//                 <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.discount && (
//                 <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
//                 </th>
//               )}
//               {columnVisibility.amount && (
//                 <th style={{ padding: '10px', textAlign: 'right', fontSize: `${fonts.tableHeaders.size}px`, fontFamily: fonts.tableHeaders.family, color: fonts.tableHeaders.color, fontWeight: fonts.tableHeaders.fontWeight, borderBottom: '2px solid #ddd' }}>
//                   <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
//                 </th>
//               )}
//             </tr>
//           </thead>

//           <tbody>
//             {data.items?.map((item, index) => (
//               <tr key={item.id}>
//                 {columnVisibility.srNo && <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{index + 1}</td>}
//                 {columnVisibility.item && (
//                   <td style={{ padding: '12px 10px', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>
//                     <div style={{ fontWeight: 'bold' }}>{item.name}</div>
//                     {item.description && <div style={{ fontSize: '11px', color: '#666' }}>{item.description}</div>}
//                   </td>
//                 )}
//                 {columnVisibility.hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.hsn}</td>}
//                 {columnVisibility.qty && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.qty}</td>}
//                 {columnVisibility.rate && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{formatCurrency(item.rate)}</td>}
//                 {columnVisibility.gst && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.gst_rate}%</td>}
//                 {columnVisibility.discount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{item.discount}</td>}
//                 {columnVisibility.roundOff && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{formatCurrency(item.roundOff)}</td>}
//                 {columnVisibility.amount && <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: `${fonts.tableContent.size}px`, fontFamily: fonts.tableContent.family, color: fonts.tableContent.color, fontWeight: fonts.tableContent.fontWeight, borderBottom: '1px solid #e0e0e0' }}>{formatCurrency(item.amount)}</td>}

//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {/* Items Table */}
//         {/* <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px', border: '1px solid #e0e7ff' }}>
//           <thead>
//             <tr style={{ background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)' }}>
//               {columnVisibility.srNo && <th style={{ padding: '12px 15px', textAlign: 'left', ...f('tableHeaders') }}>#</th>}
//               {columnVisibility.item && <th style={{ padding: '12px 15px', textAlign: 'left', ...f('tableHeaders') }}>{headerLabels.item}</th>}
//               {columnVisibility.hsn && <th style={{ padding: '12px 15px', textAlign: 'center', ...f('tableHeaders') }}>{headerLabels.hsn}</th>}
//               {columnVisibility.qty && <th style={{ padding: '12px 15px', textAlign: 'right', ...f('tableHeaders') }}>{headerLabels.qty}</th>}
//               {columnVisibility.rate && <th style={{ padding: '12px 15px', textAlign: 'right', ...f('tableHeaders') }}>{headerLabels.rate}</th>}
//               {columnVisibility.gst && <th style={{ padding: '12px 15px', textAlign: 'right', ...f('tableHeaders') }}>{headerLabels.gst}</th>}
//               {columnVisibility.discount && <th style={{ padding: '12px 15px', textAlign: 'right', ...f('tableHeaders') }}>{headerLabels.discount}</th>}
//               {columnVisibility.amount && <th style={{ padding: '12px 15px', textAlign: 'right', ...f('tableHeaders') }}>{headerLabels.amount}</th>}
//             </tr>
//           </thead>
//           <tbody>
//             {data.items?.map((item, index) => (
//               <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f9ff' }}>
//                 {columnVisibility.srNo && <td style={{ padding: '15px', ...f('tableContent') }}>{index + 1}</td>}
//                 {columnVisibility.item && (
//                   <td style={{ padding: '15px', ...f('tableContent') }}>
//                     <div style={{ fontWeight: '600', marginBottom: '4px' }}>{item.name}</div>
//                     {item.description && <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal' }}>{item.description}</div>}
//                   </td>
//                 )}
//                 {columnVisibility.hsn && <td style={{ padding: '15px', textAlign: 'center', ...f('tableContent') }}>{item.hsn}</td>}
//                 {columnVisibility.qty && <td style={{ padding: '15px', textAlign: 'right', ...f('tableContent') }}>{item.qty}</td>}
//                 {columnVisibility.rate && <td style={{ padding: '15px', textAlign: 'right', ...f('tableContent') }}>₹{parseFloat(item.rate).toFixed(2)}</td>}
//                 {columnVisibility.gst && <td style={{ padding: '15px', textAlign: 'right', ...f('tableContent') }}>{item.gst_rate}%</td>}
//                 {columnVisibility.discount && <td style={{ padding: '15px', textAlign: 'right', ...f('tableContent') }}>{item.discount}</td>}
//                 {columnVisibility.amount && <td style={{ padding: '15px', textAlign: 'right', ...f('tableContent') }}>₹{parseFloat(item.amount).toFixed(2)}</td>}
//               </tr>
//             ))}
//           </tbody>
//         </table> */}

//         {/* Totals & Bank Info */}
//         <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
//           {/* Bank Info */}
//           <div style={{ width: '48%', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px', }}>
//             <h4 style={{
//               margin: '0 0 10px 0',
//               ...f('sectionHeaders'),
//               textTransform: 'uppercase'
//             }}>Bank Details</h4>
//             <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>Bank:</span> {data.bankInfo?.bankName}</p>
//             <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c Name:</span> {data.bankInfo?.accountholdername}</p>
//             <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>A/c No:</span> {data.bankInfo?.accountNumber}</p>
//             <p style={{ margin: '4px 0', fontSize: '13px', color: '#1e293b' }}><span style={{ fontWeight: '600' }}>IFSC:</span> {data.bankInfo?.ifscCode}</p>
//           </div>

//           {/* Totals */}
//           <div style={{ width: '48%', display: 'flex', justifyContent: 'flex-end' }}>
//             <div style={{ minWidth: '300px', border: '2px solid #052558ff', padding: '15px', borderRadius: '8px', }}>
//               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                 <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
//                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
//               </div>
//               {data.cgst > 0 && (
//                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                   <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
//                   <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
//                 </div>
//               )}
//               {data.sgst > 0 && (
//                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                   <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
//                   <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
//                 </div>
//               )}
//               {data.igst > 0 && (
//                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                   <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
//                   <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
//                 </div>
//               )}
//               <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                 <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
//                 <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>₹{data.discount}</span>
//               </div>
//               {data.roundOff != null && data.roundOff !== 0 && (
//                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #bfdbfe' }}>
//                   <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
//                   <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>{formatCurrency(data.roundOff)}</span>
//                 </div>
//               )}

//               <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #3b82f6', marginTop: '12px' }}>
//                 <span style={f('totals')}>TOTAL</span>
//                 <span style={f('totals')}>{formatCurrency(data.total)}</span>
//               </div>

//             </div>
//           </div>

//         </div>
//         <div style={{
//           marginTop: '16px',
//           padding: '12px',
//           backgroundColor: '#f0f9ff',
//           borderRadius: '6px',
//           textAlign: 'center'
//         }}>
//           <span style={{
//             fontSize: '14px',
//             fontWeight: '600',
//             color: '#1e40af',
//             fontStyle: 'italic'
//           }}>
//             Amount in Words: {totalInWords}
//           </span>
//         </div>

//         {/* Signature Section */}
//         <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
//           {signatureImage ? (
//             <div
//               style={{
//                 position: 'relative',
//                 left: `${signaturePosition.x}px`,
//                 cursor: 'move',
//                 display: 'inline-block',
//                 border: '2px dashed transparent',
//                 transition: 'border 0.2s'
//               }}
//               draggable
//               onDragEnd={onSignatureDrag}
//               onMouseOver={(e) => e.currentTarget.style.border = `2px dashed ${accentHover}`}
//               onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
//             >
//               <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
//               <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
//                 Authorized Signatory
//               </div>
//             </div>
//           ) : (
//             <div style={{ textAlign: 'right' }}>
//               <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
//                 <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

const ElegantTemplate = ({
  data,
  columnVisibility = { srNo: true, item: true, hsn: true, qty: true, rate: true, gst: true, discount: true, amount: true },
  headerLabels = { item: 'DESCRIPTION', hsn: 'HSN/SAC', qty: 'QTY', rate: 'RATE', gst: 'GST %', discount: 'DISCOUNT', amount: 'AMOUNT' },
  logoSize = 100,
  logoPosition = { x: 350, y: 40 },
  signatureImage,
  signaturePosition = { x: 500, y: 900 },
  onLogoDrag,
  onSignatureDrag,
  fontSettings = {},
  columnWidths = {},
  onColumnResize,
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  // This distributes available width fairly when user resizes
  const getColumnWidth = (key) => {
    const saved = columnWidths[key];
    if (saved) return saved;

    // Default fair distribution (total ~100%)
    const defaults = {
      srNo: '5%',
      item: '32%',
      hsn: '10%',
      qty: '8%',
      rate: '10%',
      gst: '8%',
      discount: '10%',
      amount: '12%'
    };
    return defaults[key] || '10%';
  };

  // Smart flex column that auto-scales
  const FlexCol = ({ children, width, textAlign = 'left', styles = {}, bold = false }) => (
    <div style={{
      flex: `0 0 ${width}`,
      paddingRight: '8px',
      textAlign,
      fontWeight: bold ? 'bold' : 'normal',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      ...styles
    }}>
      {children}
    </div>
  );
  // Default font settings using Classic keys and elegant styles
  const defaultFontSettings = {
    companyName: { size: 30, family: 'Garamond, serif', color: '#334155', fontWeight: '300' },
    invoiceTitle: { size: 18, family: 'Garamond, serif', color: '#64748b', fontWeight: '300' },
    sectionHeaders: { size: 14, family: 'Garamond, serif', color: '#334155', fontWeight: '600' },
    tableHeaders: { size: 12, family: 'Garamond, serif', color: '#94a3b8', fontWeight: '600' },
    tableContent: { size: 14, family: 'Garamond, serif', color: '#1e293b', fontWeight: 'normal' },
    totals: { size: 18, family: 'Garamond, serif', color: '#334155', fontWeight: '700' }
  };

  // Use robust font merging
  const fonts = mergeFonts(defaultFontSettings, fontSettings);

  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertBelowThousand(n % 100) : '');
    };

    let str = '';
    num = Math.round(num * 100) / 100;
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);

    if (rupees > 0) {
      if (rupees >= 10000000) {
        str += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
      }
      if (rupees % 10000000 >= 100000) {
        str += convertBelowThousand(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
      }
      if (rupees % 100000 >= 1000) {
        str += convertBelowThousand(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
      }
      str += convertBelowThousand(rupees % 1000);
      str += ' Rupees';
    }

    if (paise > 0) {
      str += (rupees > 0 ? ' and ' : '') + convertBelowThousand(paise) + ' Paise';
    }

    return str.trim() + ' Only';
  };

  const totalInWords = numberToWords(parseFloat(data.total || 0));


  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '₹0.00';

    const num = parseFloat(String(value).replace(/,/g, ''));

    if (isNaN(num)) return '₹0.00';

    return '₹' + num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const f = (key) => ({
    fontSize: `${fonts[key].size}px`,
    fontFamily: fonts[key].family,
    color: fonts[key].color,
    fontWeight: fonts[key].fontWeight,
  });

  return (
    <div style={{
      fontFamily: 'Garamond, serif',
      padding: '40px',
      backgroundColor: '#fff',
      minHeight: '400px',
      border: '3px double #cbd5e1',
      position: 'relative'
    }}>

      {/* Fixed Logo */}
      {data.company_logo && (
        <div
          style={{
            position: 'absolute',
            right: '40px',
            top: '40px',
            zIndex: 100
          }}
        >
          <img
            src={data.company_logo}
            alt="Company Logo"
            style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
          />
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #cbd5e1' }}>
        {data.billFrom?.name && (
          <div style={{ marginBottom: '15px' }}>
            <p style={{ margin: '8px 0 0 0', fontSize: '16px', color: '#64748b', letterSpacing: '1px' }}>
              {data.entity_name}
            </p>
          </div>
        )}
        <h1 style={{ margin: 0, ...f('invoiceTitle'), letterSpacing: '3px', textTransform: 'uppercase' }}>
          {data.invoice?.documentType || 'INVOICE'}
        </h1>
        <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#64748b', letterSpacing: '2px' }}>
          {data.invoice?.number}
        </p>
      </div>

      {/* Dispatch From */}
      {data.dispatchFrom && (
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f8fafc',
          borderLeft: '3px solid #cbd5e1',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 8px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>DISPATCH FROM</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.dispatchFrom?.name}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.dispatchFrom?.address}</p>
        </div>
      )}

      {/* Bill From / Bill To */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ width: '45%' }}>
          <p style={{ margin: '0 0 5px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>ISSUED BY</p>
          <p style={{ margin: '8px 0 2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.billFrom?.name}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.address}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.location}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.state}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.pincode}</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>GSTIN: {data.billFrom?.gstin}</p>
        </div>
        <div style={{ width: '45%', textAlign: 'right' }}>
          <p style={{ margin: '0 0 5px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>INVOICED TO</p>
          <p style={{ margin: '8px 0 2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.billTo?.name}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billTo?.address}</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
        </div>
      </div>

      {/* Ship To */}
      {data.shipTo && (
        <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ margin: '0 0 8px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>SHIP TO</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.shipTo.name}</p>
          <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.shipTo.address}</p>
        </div>
      )}

      {/* Invoice Details */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', padding: '15px', backgroundColor: '#f8fafc' }}>
        {data.invoice?.documentType === "Invoice" && (<div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Invoice Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.date}</p></div>)}
        {data.invoice?.documentType === "Bill" && (<div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Bill Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.date}</p></div>)}

        {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
          <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Original Invoice Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.originalDate}</p></div>
        )}
        {data.invoice?.originalNumber && data.invoice.originalNumber !== 'null' && (
          <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Original Invoice Number</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.originalNumber}</p></div>
        )}
        {data.invoice?.dueDate && (
          <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Due Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{formatDate(data.invoice?.dueDate)}</p></div>
        )}
        <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Place of Supply</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.placeOfSupply}</p></div>
        {/* <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Amount Due</p><p style={{ margin: '4px 0 0 0', ...f('totals') }}>₹{parseFloat(data.total).toFixed(2)}</p></div> */}
      </div>

      {/* Reverse Charge */}
      {data.invoice?.reverseCharge === 'Yes' && (
        <div style={{
          marginBottom: '20px',
          padding: '10px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: '#dc2626', fontWeight: '600', letterSpacing: '1px' }}>
            REVERSE CHARGE APPLICABLE
          </p>
        </div>
      )}

      {/* Items Table */}
      {/* <div style={{ marginBottom: '30px' }}>
        <div style={{
          display: 'flex',
          padding: '12px 0',
          borderBottom: '2px solid #cbd5e1',
          ...f('tableHeaders'),
          letterSpacing: '1px',
          textTransform: 'uppercase',
          borderCollapse: 'separate'
        }}>
          {columnVisibility.srNo && <div style={{ width: '40px' }}>#</div>}
          {columnVisibility.item && <div style={{ flex: 1 }}>{headerLabels.item}</div>}
          {columnVisibility.hsn && <div style={{ width: '90px', textAlign: 'center' }}>{headerLabels.hsn}</div>}
          {columnVisibility.qty && <div style={{ width: '60px', textAlign: 'right' }}>{headerLabels.qty}</div>}
          {columnVisibility.rate && <div style={{ width: '80px', textAlign: 'right' }}>{headerLabels.rate}</div>}
          {columnVisibility.gst && <div style={{ width: '70px', textAlign: 'right' }}>{headerLabels.gst}</div>}
          {columnVisibility.discount && <div style={{ width: '80px', textAlign: 'right' }}>{headerLabels.discount}</div>}
          {columnVisibility.amount && <div style={{ width: '100px', textAlign: 'right' }}>{headerLabels.amount}</div>}
        </div>

        {data.items?.map((item, index) => (
          <div key={item.id} style={{
            padding: '15px 0',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            {columnVisibility.srNo && <div style={{ width: '40px', ...f('tableContent') }}>{index + 1}</div>}
            {columnVisibility.item && (
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, ...f('tableContent'), fontWeight: '600' }}>{item.name}</p>
                {item.description && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{item.description}</p>
                )}
              </div>
            )}
            {columnVisibility.hsn && <div style={{ width: '90px', ...f('tableContent'), textAlign: 'center' }}>{item.hsn}</div>}
            {columnVisibility.qty && <div style={{ width: '60px', ...f('tableContent'), textAlign: 'right' }}>{item.qty}</div>}
            {columnVisibility.rate && <div style={{ width: '80px', ...f('tableContent'), textAlign: 'right' }}>₹{parseFloat(item.rate).toFixed(2)}</div>}
            {columnVisibility.gst && <div style={{ width: '70px', ...f('tableContent'), textAlign: 'right' }}>{item.gst_rate}%</div>}
            {columnVisibility.discount && <div style={{ width: '80px', ...f('tableContent'), textAlign: 'right' }}>{item.discount}</div>}
            {columnVisibility.amount && <div style={{ width: '100px', ...f('tableContent'), fontWeight: '600', textAlign: 'right' }}>₹{parseFloat(item.amount).toFixed(2)}</div>}
          </div>
        ))}
      </div> */}
      {/* <div style={{ marginBottom: '30px' }}>
        <div style={{
          display: 'flex',
          padding: '12px 8px',
          borderBottom: '2px solid #cbd5e1',
          ...f('tableHeaders'),
          letterSpacing: '1px',
          textTransform: 'uppercase',
          background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)',
          color: '#ffffff'
        }}>
          {columnVisibility.srNo && (
            <div style={{ width: `${columnWidths.srNo || 40}px`, position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
            </div>
          )}
          {columnVisibility.item && (
            <div style={{ width: `${columnWidths.item || 280}px`, position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
            </div>
          )}
          {columnVisibility.hsn && (
            <div style={{ width: `${columnWidths.hsn || 90}px`, textAlign: 'center', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
            </div>
          )}
          {columnVisibility.qty && (
            <div style={{ width: `${columnWidths.qty || 60}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
            </div>
          )}
          {columnVisibility.rate && (
            <div style={{ width: `${columnWidths.rate || 80}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
            </div>
          )}
          {columnVisibility.gst && (
            <div style={{ width: `${columnWidths.gst || 70}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
            </div>
          )}
          {columnVisibility.discount && (
            <div style={{ width: `${columnWidths.discount || 80}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
            </div>
          )}
          {columnVisibility.amount && (
            <div style={{ width: `${columnWidths.amount || 100}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
              <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
            </div>
          )}
        </div>
        {data.items?.map((item, index) => (
          <div key={item.id} style={{
            padding: '15px 8px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            {columnVisibility.srNo && <div style={{ width: `${columnWidths.srNo || 40}px`, ...f('tableContent'), paddingRight: '8px' }}>{index + 1}</div>}
            {columnVisibility.item && (
              <div style={{ width: `${columnWidths.item || 280}px`, paddingRight: '8px' }}>
                <p style={{ margin: 0, ...f('tableContent'), fontWeight: '600' }}>{item.name}</p>
                {item.description && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{item.description}</p>
                )}
              </div>
            )}
            {columnVisibility.hsn && <div style={{ width: `${columnWidths.hsn || 90}px`, ...f('tableContent'), textAlign: 'center', paddingRight: '8px' }}>{item.hsn}</div>}
            {columnVisibility.qty && <div style={{ width: `${columnWidths.qty || 60}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.qty}</div>}
            {columnVisibility.rate && <div style={{ width: `${columnWidths.rate || 80}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>₹{parseFloat(item.rate).toFixed(2)}</div>}
            {columnVisibility.gst && <div style={{ width: `${columnWidths.gst || 70}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.gst_rate}%</div>}
            {columnVisibility.discount && <div style={{ width: `${columnWidths.discount || 80}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.discount}</div>}
            {columnVisibility.amount && <div style={{ width: `${columnWidths.amount || 100}px`, ...f('tableContent'), fontWeight: '600', textAlign: 'right', paddingRight: '8px' }}>₹{parseFloat(item.amount).toFixed(2)}</div>}
          </div>
        ))}
      </div> */}

      <div style={{ marginBottom: '30px', overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* HEADER */}
          <div style={{
            display: 'flex',
            padding: '12px 8px',
            background: 'linear-gradient(135deg, #c0c3cfff 0%, rgba(235, 239, 244, 1) 100%)',
            color: '#ffffff',
            ...f('tableHeaders'),
            fontWeight: 'bold',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            borderBottom: '2px solid #cbd5e1'
          }}>
            {columnVisibility.srNo && (
              <div style={{
                width: columnWidths.srNo || '50px',
                minWidth: columnWidths.srNo || '50px',
                maxWidth: columnWidths.srNo || '50px',
                paddingRight: '8px',
                position: 'relative',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="srNo" onResize={onColumnResize} currentWidth={columnWidths.srNo}>
                  #
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.item && (
              <div style={{
                width: columnWidths.item || '280px',
                minWidth: columnWidths.item || '280px',
                maxWidth: columnWidths.item || '280px',
                paddingRight: '8px',
                position: 'relative',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="item" onResize={onColumnResize} currentWidth={columnWidths.item}>
                  {headerLabels.item}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.hsn && (
              <div style={{
                width: columnWidths.hsn || '90px',
                minWidth: columnWidths.hsn || '90px',
                maxWidth: columnWidths.hsn || '90px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'center',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="hsn" onResize={onColumnResize} currentWidth={columnWidths.hsn}>
                  {headerLabels.hsn}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.qty && (
              <div style={{
                width: columnWidths.qty || '80px',
                minWidth: columnWidths.qty || '80px',
                maxWidth: columnWidths.qty || '80px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'right',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="qty" onResize={onColumnResize} currentWidth={columnWidths.qty}>
                  {headerLabels.qty}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.rate && (
              <div style={{
                width: columnWidths.rate || '100px',
                minWidth: columnWidths.rate || '100px',
                maxWidth: columnWidths.rate || '100px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'right',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="rate" onResize={onColumnResize} currentWidth={columnWidths.rate}>
                  {headerLabels.rate}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.gst && (
              <div style={{
                width: columnWidths.gst || '80px',
                minWidth: columnWidths.gst || '80px',
                maxWidth: columnWidths.gst || '80px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'right',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="gst" onResize={onColumnResize} currentWidth={columnWidths.gst}>
                  {headerLabels.gst}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.discount && (
              <div style={{
                width: columnWidths.discount || '100px',
                minWidth: columnWidths.discount || '100px',
                maxWidth: columnWidths.discount || '100px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'right',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="discount" onResize={onColumnResize} currentWidth={columnWidths.discount}>
                  {headerLabels.discount}
                </ResizableHeader>
              </div>
            )}

            {columnVisibility.amount && (
              <div style={{
                width: columnWidths.amount || '120px',
                minWidth: columnWidths.amount || '120px',
                maxWidth: columnWidths.amount || '120px',
                paddingRight: '8px',
                position: 'relative',
                textAlign: 'right',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                <ResizableHeader columnKey="amount" onResize={onColumnResize} currentWidth={columnWidths.amount}>
                  {headerLabels.amount}
                </ResizableHeader>
              </div>
            )}
          </div>

          {/* ROWS */}
          {data.items?.map((item, index) => (
            <div key={item.id} style={{
              display: 'flex',
              padding: '15px 8px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
              alignItems: 'flex-start'
            }}>
              {columnVisibility.srNo && (
                <div style={{
                  width: columnWidths.srNo || '50px',
                  minWidth: columnWidths.srNo || '50px',
                  maxWidth: columnWidths.srNo || '50px',
                  paddingRight: '8px',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  {index + 1}
                </div>
              )}

              {columnVisibility.item && (
                <div style={{
                  width: columnWidths.item || '280px',
                  minWidth: columnWidths.item || '280px',
                  maxWidth: columnWidths.item || '280px',
                  paddingRight: '8px',
                  flexShrink: 0
                }}>
                  <div style={{ ...f('tableContent'), fontWeight: '600' }}>{item.name}</div>
                  {/* {item.description && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                      {item.description}
                    </div>
                  )} */}
                  {item.line_item_description && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{item.line_item_description}</div>}
                </div>
              )}

              {columnVisibility.hsn && (
                <div style={{
                  width: columnWidths.hsn || '90px',
                  minWidth: columnWidths.hsn || '90px',
                  maxWidth: columnWidths.hsn || '90px',
                  paddingRight: '8px',
                  textAlign: 'center',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  {item.hsn}
                </div>
              )}

              {columnVisibility.qty && (
                <div style={{
                  width: columnWidths.qty || '80px',
                  minWidth: columnWidths.qty || '80px',
                  maxWidth: columnWidths.qty || '80px',
                  paddingRight: '8px',
                  textAlign: 'right',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  {item.qty}
                </div>
              )}

              {columnVisibility.rate && (
                <div style={{
                  width: columnWidths.rate || '100px',
                  minWidth: columnWidths.rate || '100px',
                  maxWidth: columnWidths.rate || '100px',
                  paddingRight: '8px',
                  textAlign: 'right',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  ₹{formatCurrency(item.rate || 0)}
                </div>
              )}

              {columnVisibility.gst && (
                <div style={{
                  width: columnWidths.gst || '80px',
                  minWidth: columnWidths.gst || '80px',
                  maxWidth: columnWidths.gst || '80px',
                  paddingRight: '8px',
                  textAlign: 'right',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  {item.gst_rate}%
                </div>
              )}

              {columnVisibility.discount && (
                <div style={{
                  width: columnWidths.discount || '100px',
                  minWidth: columnWidths.discount || '100px',
                  maxWidth: columnWidths.discount || '100px',
                  paddingRight: '8px',
                  textAlign: 'right',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  {formatCurrency(item.discount || '-')}
                </div>
              )}

              {columnVisibility.amount && (
                <div style={{
                  width: columnWidths.amount || '120px',
                  minWidth: columnWidths.amount || '120px',
                  maxWidth: columnWidths.amount || '120px',
                  paddingRight: '8px',
                  textAlign: 'right',
                  fontWeight: 'bold',
                  ...f('tableContent'),
                  flexShrink: 0
                }}>
                  ₹{formatCurrency(item.amount || 0)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bank Details + Totals */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
        <div style={{ width: '45%' }}>
          <div style={{ border: '1px solid #cbd5e1', padding: '15px', backgroundColor: '#f8fafc' }}>
            <p style={{ margin: '0 0 12px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>BANK DETAILS</p>
            <div style={{ marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Bank</p>
              <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.bankName}</p>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Account Holder Name</p>
              <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.accountholdername}</p>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Account Number</p>
              <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.accountNumber}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>IFSC Code</p>
              <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.ifscCode}</p>
            </div>
          </div>
        </div>

        <div style={{ width: '45%', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ minWidth: '280px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
            </div>
            {data.cgst > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
              </div>
            )}
            {data.sgst > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
              </div>
            )}
            {data.igst > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
              </div>
            )}
            {data.otherCharges > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Other Charges</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.otherCharges)}</span>
              </div>
            )}
            {data.roundOff != null && data.roundOff !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>{formatCurrency(data.roundOff)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>₹{data.discount || '0.00'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderTop: '2px solid #334155', marginTop: '10px' }}>
              <span style={{ ...f('totals'), letterSpacing: '1px' }}>TOTAL</span>
              <span style={f('totals')}>{formatCurrency(data.total)}</span>
            </div>

            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f8fafc',
              border: '1px dashed #cbd5e1',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <span style={{
                fontSize: '14px',
                fontFamily: fonts.totals.family,
                fontStyle: 'italic',
                color: '#334155',
                fontWeight: '600',
                letterSpacing: '0.5px'
              }}>
                Amount in Words: {totalInWords}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Draggable Signature */}
      {/* Signature Section - Fixed at bottom with proper spacing */}
      <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
        {signatureImage ? (
          <div
            style={{
              position: 'relative',
              left: `${signaturePosition.x}px`,
              cursor: 'move',
              display: 'inline-block',
              border: '2px dashed transparent',
              transition: 'border 0.2s'
            }}
            draggable
            onDragEnd={onSignatureDrag}
            onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
            onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
          >
            <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
              Authorized Signatory
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // return (
  //   <div style={{
  //     fontFamily: 'Garamond, serif',
  //     padding: '40px',
  //     backgroundColor: '#fff',
  //     minHeight: '400px',
  //     border: '3px double #cbd5e1',
  //     position: 'relative'
  //   }}>

  //     {/* Draggable Logo */}
  //     {data.company_logo && (
  //       <div
  //         style={{
  //           position: 'absolute',
  //           left: `${logoPosition.x}px`,
  //           top: `${logoPosition.y}px`,
  //           cursor: 'move',
  //           zIndex: 100,
  //           border: '2px dashed transparent',
  //           transition: 'border 0.2s'
  //         }}
  //         draggable
  //         onDragEnd={onLogoDrag}
  //         onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #94a3b8'}
  //         onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //       >
  //         <img
  //           src={data.company_logo}
  //           alt="Company Logo"
  //           style={{ width: `${logoSize}px`, height: 'auto', objectFit: 'contain' }}
  //         />
  //       </div>
  //     )}

  //     {/* Header */}
  //     <div style={{ textAlign: 'center', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #cbd5e1' }}>
  //       {data.billFrom?.name && (
  //         <div style={{ marginBottom: '15px' }}>
  //           <p style={{ margin: '8px 0 0 0', fontSize: '16px', color: '#64748b', letterSpacing: '1px' }}>
  //             {data.entity_name}
  //           </p>
  //         </div>
  //       )}
  //       <h1 style={{ margin: 0, ...f('invoiceTitle'), letterSpacing: '3px', textTransform: 'uppercase' }}>
  //         {data.invoice?.documentType || 'INVOICE'}
  //       </h1>
  //       <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#64748b', letterSpacing: '2px' }}>
  //         {data.invoice?.number}
  //       </p>
  //     </div>

  //     {/* Dispatch From */}
  //     {data.dispatchFrom && (
  //       <div style={{
  //         marginBottom: '20px',
  //         padding: '15px',
  //         backgroundColor: '#f8fafc',
  //         borderLeft: '3px solid #cbd5e1',
  //         textAlign: 'center'
  //       }}>
  //         <p style={{ margin: '0 0 8px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>DISPATCH FROM</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.dispatchFrom?.name}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.dispatchFrom?.address}</p>
  //       </div>
  //     )}

  //     {/* Bill From / Bill To */}
  //     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
  //       <div style={{ width: '45%' }}>
  //         <p style={{ margin: '0 0 5px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>ISSUED BY</p>
  //         <p style={{ margin: '8px 0 2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.billFrom?.name}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.address}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.location}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.state}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billFrom?.pincode}</p>
  //         <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>GSTIN: {data.billFrom?.gstin}</p>
  //       </div>
  //       <div style={{ width: '45%', textAlign: 'right' }}>
  //         <p style={{ margin: '0 0 5px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>INVOICED TO</p>
  //         <p style={{ margin: '8px 0 2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.billTo?.name}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.billTo?.address}</p>
  //         <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>GSTIN: {data.billTo?.gstin}</p>
  //       </div>
  //     </div>

  //     {/* Ship To */}
  //     {data.shipTo && (
  //       <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>
  //         <p style={{ margin: '0 0 8px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>SHIP TO</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), fontWeight: '600' }}>{data.shipTo.name}</p>
  //         <p style={{ margin: '2px 0', ...f('tableContent'), lineHeight: '1.6' }}>{data.shipTo.address}</p>
  //       </div>
  //     )}

  //     {/* Invoice Details */}
  //     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', padding: '15px', backgroundColor: '#f8fafc' }}>
  //       {data.invoice?.documentType === "Invoice" && (<div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Invoice Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.date}</p></div>)}
  //       {data.invoice?.documentType === "Bill" && (<div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Bill Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.date}</p></div>)}

  //       {data.invoice?.originalDate && data.invoice?.originalDate !== 'null' && (
  //         <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Original Invoice Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.originalDate}</p></div>
  //       )}
  //       {data.invoice?.originalNumber && data.invoice.originalNumber !== 'null' && (
  //         <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Original Invoice Number</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.invoice?.originalNumber}</p></div>
  //       )}
  //       {data.invoice?.dueDate && (
  //         <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Due Date</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{formatDate(data.invoice?.dueDate)}</p></div>
  //       )}
  //       <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Place of Supply</p><p style={{ margin: '4px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '14px' }}>{data.placeOfSupply}</p></div>
  //       {/* <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Amount Due</p><p style={{ margin: '4px 0 0 0', ...f('totals') }}>₹{parseFloat(data.total).toFixed(2)}</p></div> */}
  //     </div>

  //     {/* Reverse Charge */}
  //     {data.invoice?.reverseCharge === 'Yes' && (
  //       <div style={{
  //         marginBottom: '20px',
  //         padding: '10px',
  //         backgroundColor: '#fef2f2',
  //         border: '1px solid #fca5a5',
  //         textAlign: 'center'
  //       }}>
  //         <p style={{ margin: 0, fontSize: '12px', color: '#dc2626', fontWeight: '600', letterSpacing: '1px' }}>
  //           REVERSE CHARGE APPLICABLE
  //         </p>
  //       </div>
  //     )}

  //     {/* Items Table */}
  //     {/* <div style={{ marginBottom: '30px' }}>
  //       <div style={{
  //         display: 'flex',
  //         padding: '12px 0',
  //         borderBottom: '2px solid #cbd5e1',
  //         ...f('tableHeaders'),
  //         letterSpacing: '1px',
  //         textTransform: 'uppercase',
  //         borderCollapse: 'separate'
  //       }}>
  //         {columnVisibility.srNo && <div style={{ width: '40px' }}>#</div>}
  //         {columnVisibility.item && <div style={{ flex: 1 }}>{headerLabels.item}</div>}
  //         {columnVisibility.hsn && <div style={{ width: '90px', textAlign: 'center' }}>{headerLabels.hsn}</div>}
  //         {columnVisibility.qty && <div style={{ width: '60px', textAlign: 'right' }}>{headerLabels.qty}</div>}
  //         {columnVisibility.rate && <div style={{ width: '80px', textAlign: 'right' }}>{headerLabels.rate}</div>}
  //         {columnVisibility.gst && <div style={{ width: '70px', textAlign: 'right' }}>{headerLabels.gst}</div>}
  //         {columnVisibility.discount && <div style={{ width: '80px', textAlign: 'right' }}>{headerLabels.discount}</div>}
  //         {columnVisibility.amount && <div style={{ width: '100px', textAlign: 'right' }}>{headerLabels.amount}</div>}
  //       </div>

  //       {data.items?.map((item, index) => (
  //         <div key={item.id} style={{
  //           padding: '15px 0',
  //           borderBottom: '1px solid #e2e8f0',
  //           display: 'flex',
  //           alignItems: 'flex-start'
  //         }}>
  //           {columnVisibility.srNo && <div style={{ width: '40px', ...f('tableContent') }}>{index + 1}</div>}
  //           {columnVisibility.item && (
  //             <div style={{ flex: 1 }}>
  //               <p style={{ margin: 0, ...f('tableContent'), fontWeight: '600' }}>{item.name}</p>
  //               {item.description && (
  //                 <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{item.description}</p>
  //               )}
  //             </div>
  //           )}
  //           {columnVisibility.hsn && <div style={{ width: '90px', ...f('tableContent'), textAlign: 'center' }}>{item.hsn}</div>}
  //           {columnVisibility.qty && <div style={{ width: '60px', ...f('tableContent'), textAlign: 'right' }}>{item.qty}</div>}
  //           {columnVisibility.rate && <div style={{ width: '80px', ...f('tableContent'), textAlign: 'right' }}>₹{parseFloat(item.rate).toFixed(2)}</div>}
  //           {columnVisibility.gst && <div style={{ width: '70px', ...f('tableContent'), textAlign: 'right' }}>{item.gst_rate}%</div>}
  //           {columnVisibility.discount && <div style={{ width: '80px', ...f('tableContent'), textAlign: 'right' }}>{item.discount}</div>}
  //           {columnVisibility.amount && <div style={{ width: '100px', ...f('tableContent'), fontWeight: '600', textAlign: 'right' }}>₹{parseFloat(item.amount).toFixed(2)}</div>}
  //         </div>
  //       ))}
  //     </div> */}
  //     {/* <div style={{ marginBottom: '30px' }}>
  //       <div style={{
  //         display: 'flex',
  //         padding: '12px 8px',
  //         borderBottom: '2px solid #cbd5e1',
  //         ...f('tableHeaders'),
  //         letterSpacing: '1px',
  //         textTransform: 'uppercase',
  //         background: 'linear-gradient(135deg, #c0c3cfff 0%, #05295aff 100%)',
  //         color: '#ffffff'
  //       }}>
  //         {columnVisibility.srNo && (
  //           <div style={{ width: `${columnWidths.srNo || 40}px`, position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="srNo" onResize={onColumnResize}>#</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.item && (
  //           <div style={{ width: `${columnWidths.item || 280}px`, position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="item" onResize={onColumnResize}>{headerLabels.item}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.hsn && (
  //           <div style={{ width: `${columnWidths.hsn || 90}px`, textAlign: 'center', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="hsn" onResize={onColumnResize}>{headerLabels.hsn}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.qty && (
  //           <div style={{ width: `${columnWidths.qty || 60}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="qty" onResize={onColumnResize}>{headerLabels.qty}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.rate && (
  //           <div style={{ width: `${columnWidths.rate || 80}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="rate" onResize={onColumnResize}>{headerLabels.rate}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.gst && (
  //           <div style={{ width: `${columnWidths.gst || 70}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="gst" onResize={onColumnResize}>{headerLabels.gst}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.discount && (
  //           <div style={{ width: `${columnWidths.discount || 80}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="discount" onResize={onColumnResize}>{headerLabels.discount}</ResizableHeader>
  //           </div>
  //         )}
  //         {columnVisibility.amount && (
  //           <div style={{ width: `${columnWidths.amount || 100}px`, textAlign: 'right', position: 'relative', paddingRight: '8px', minHeight: '24px' }}>
  //             <ResizableHeader columnKey="amount" onResize={onColumnResize}>{headerLabels.amount}</ResizableHeader>
  //           </div>
  //         )}
  //       </div>
  //       {data.items?.map((item, index) => (
  //         <div key={item.id} style={{
  //           padding: '15px 8px',
  //           borderBottom: '1px solid #e2e8f0',
  //           display: 'flex',
  //           alignItems: 'flex-start'
  //         }}>
  //           {columnVisibility.srNo && <div style={{ width: `${columnWidths.srNo || 40}px`, ...f('tableContent'), paddingRight: '8px' }}>{index + 1}</div>}
  //           {columnVisibility.item && (
  //             <div style={{ width: `${columnWidths.item || 280}px`, paddingRight: '8px' }}>
  //               <p style={{ margin: 0, ...f('tableContent'), fontWeight: '600' }}>{item.name}</p>
  //               {item.description && (
  //                 <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{item.description}</p>
  //               )}
  //             </div>
  //           )}
  //           {columnVisibility.hsn && <div style={{ width: `${columnWidths.hsn || 90}px`, ...f('tableContent'), textAlign: 'center', paddingRight: '8px' }}>{item.hsn}</div>}
  //           {columnVisibility.qty && <div style={{ width: `${columnWidths.qty || 60}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.qty}</div>}
  //           {columnVisibility.rate && <div style={{ width: `${columnWidths.rate || 80}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>₹{parseFloat(item.rate).toFixed(2)}</div>}
  //           {columnVisibility.gst && <div style={{ width: `${columnWidths.gst || 70}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.gst_rate}%</div>}
  //           {columnVisibility.discount && <div style={{ width: `${columnWidths.discount || 80}px`, ...f('tableContent'), textAlign: 'right', paddingRight: '8px' }}>{item.discount}</div>}
  //           {columnVisibility.amount && <div style={{ width: `${columnWidths.amount || 100}px`, ...f('tableContent'), fontWeight: '600', textAlign: 'right', paddingRight: '8px' }}>₹{parseFloat(item.amount).toFixed(2)}</div>}
  //         </div>
  //       ))}
  //     </div> */}

  //     <div style={{ marginBottom: '30px', overflow: 'hidden' }}>
  //       <div style={{
  //         display: 'flex',
  //         flexDirection: 'column',
  //         border: '1px solid #e2e8f0',
  //         borderRadius: '8px',
  //         overflow: 'hidden',
  //         boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  //       }}>
  //         {/* HEADER */}
  //         <div style={{
  //           display: 'flex',
  //           padding: '12px 8px',
  //           background: 'linear-gradient(135deg, #c0c3cfff 0%, rgba(235, 239, 244, 1) 100%)',
  //           color: '#ffffff',
  //           ...f('tableHeaders'),
  //           fontWeight: 'bold',
  //           letterSpacing: '1px',
  //           textTransform: 'uppercase',
  //           borderBottom: '2px solid #cbd5e1'
  //         }}>
  //           {columnVisibility.srNo && (
  //             <div style={{
  //               width: columnWidths.srNo || '50px',
  //               minWidth: columnWidths.srNo || '50px',
  //               maxWidth: columnWidths.srNo || '50px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="srNo" onResize={onColumnResize} currentWidth={columnWidths.srNo}>
  //                 #
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.item && (
  //             <div style={{
  //               width: columnWidths.item || '280px',
  //               minWidth: columnWidths.item || '280px',
  //               maxWidth: columnWidths.item || '280px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="item" onResize={onColumnResize} currentWidth={columnWidths.item}>
  //                 {headerLabels.item}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.hsn && (
  //             <div style={{
  //               width: columnWidths.hsn || '90px',
  //               minWidth: columnWidths.hsn || '90px',
  //               maxWidth: columnWidths.hsn || '90px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'center',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="hsn" onResize={onColumnResize} currentWidth={columnWidths.hsn}>
  //                 {headerLabels.hsn}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.qty && (
  //             <div style={{
  //               width: columnWidths.qty || '80px',
  //               minWidth: columnWidths.qty || '80px',
  //               maxWidth: columnWidths.qty || '80px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'right',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="qty" onResize={onColumnResize} currentWidth={columnWidths.qty}>
  //                 {headerLabels.qty}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.rate && (
  //             <div style={{
  //               width: columnWidths.rate || '100px',
  //               minWidth: columnWidths.rate || '100px',
  //               maxWidth: columnWidths.rate || '100px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'right',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="rate" onResize={onColumnResize} currentWidth={columnWidths.rate}>
  //                 {headerLabels.rate}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.gst && (
  //             <div style={{
  //               width: columnWidths.gst || '80px',
  //               minWidth: columnWidths.gst || '80px',
  //               maxWidth: columnWidths.gst || '80px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'right',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="gst" onResize={onColumnResize} currentWidth={columnWidths.gst}>
  //                 {headerLabels.gst}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.discount && (
  //             <div style={{
  //               width: columnWidths.discount || '100px',
  //               minWidth: columnWidths.discount || '100px',
  //               maxWidth: columnWidths.discount || '100px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'right',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="discount" onResize={onColumnResize} currentWidth={columnWidths.discount}>
  //                 {headerLabels.discount}
  //               </ResizableHeader>
  //             </div>
  //           )}

  //           {columnVisibility.amount && (
  //             <div style={{
  //               width: columnWidths.amount || '120px',
  //               minWidth: columnWidths.amount || '120px',
  //               maxWidth: columnWidths.amount || '120px',
  //               paddingRight: '8px',
  //               position: 'relative',
  //               textAlign: 'right',
  //               fontWeight: 'bold',
  //               flexShrink: 0
  //             }}>
  //               <ResizableHeader columnKey="amount" onResize={onColumnResize} currentWidth={columnWidths.amount}>
  //                 {headerLabels.amount}
  //               </ResizableHeader>
  //             </div>
  //           )}
  //         </div>

  //         {/* ROWS */}
  //         {data.items?.map((item, index) => (
  //           <div key={item.id} style={{
  //             display: 'flex',
  //             padding: '15px 8px',
  //             borderBottom: '1px solid #e2e8f0',
  //             backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
  //             alignItems: 'flex-start'
  //           }}>
  //             {columnVisibility.srNo && (
  //               <div style={{
  //                 width: columnWidths.srNo || '50px',
  //                 minWidth: columnWidths.srNo || '50px',
  //                 maxWidth: columnWidths.srNo || '50px',
  //                 paddingRight: '8px',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 {index + 1}
  //               </div>
  //             )}

  //             {columnVisibility.item && (
  //               <div style={{
  //                 width: columnWidths.item || '280px',
  //                 minWidth: columnWidths.item || '280px',
  //                 maxWidth: columnWidths.item || '280px',
  //                 paddingRight: '8px',
  //                 flexShrink: 0
  //               }}>
  //                 <div style={{ ...f('tableContent'), fontWeight: '600' }}>{item.name}</div>
  //                 {item.description && (
  //                   <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
  //                     {item.description}
  //                   </div>
  //                 )}
  //               </div>
  //             )}

  //             {columnVisibility.hsn && (
  //               <div style={{
  //                 width: columnWidths.hsn || '90px',
  //                 minWidth: columnWidths.hsn || '90px',
  //                 maxWidth: columnWidths.hsn || '90px',
  //                 paddingRight: '8px',
  //                 textAlign: 'center',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 {item.hsn}
  //               </div>
  //             )}

  //             {columnVisibility.qty && (
  //               <div style={{
  //                 width: columnWidths.qty || '80px',
  //                 minWidth: columnWidths.qty || '80px',
  //                 maxWidth: columnWidths.qty || '80px',
  //                 paddingRight: '8px',
  //                 textAlign: 'right',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 {item.qty}
  //               </div>
  //             )}

  //             {columnVisibility.rate && (
  //               <div style={{
  //                 width: columnWidths.rate || '100px',
  //                 minWidth: columnWidths.rate || '100px',
  //                 maxWidth: columnWidths.rate || '100px',
  //                 paddingRight: '8px',
  //                 textAlign: 'right',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 ₹{formatCurrency(item.rate || 0)}
  //               </div>
  //             )}

  //             {columnVisibility.gst && (
  //               <div style={{
  //                 width: columnWidths.gst || '80px',
  //                 minWidth: columnWidths.gst || '80px',
  //                 maxWidth: columnWidths.gst || '80px',
  //                 paddingRight: '8px',
  //                 textAlign: 'right',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 {item.gst_rate}%
  //               </div>
  //             )}

  //             {columnVisibility.discount && (
  //               <div style={{
  //                 width: columnWidths.discount || '100px',
  //                 minWidth: columnWidths.discount || '100px',
  //                 maxWidth: columnWidths.discount || '100px',
  //                 paddingRight: '8px',
  //                 textAlign: 'right',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 {formatCurrency(item.discount || '-')}
  //               </div>
  //             )}

  //             {columnVisibility.amount && (
  //               <div style={{
  //                 width: columnWidths.amount || '120px',
  //                 minWidth: columnWidths.amount || '120px',
  //                 maxWidth: columnWidths.amount || '120px',
  //                 paddingRight: '8px',
  //                 textAlign: 'right',
  //                 fontWeight: 'bold',
  //                 ...f('tableContent'),
  //                 flexShrink: 0
  //               }}>
  //                 ₹{formatCurrency(item.amount || 0)}
  //               </div>
  //             )}
  //           </div>
  //         ))}
  //       </div>
  //     </div>

  //     {/* Bank Details + Totals */}
  //     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
  //       <div style={{ width: '45%' }}>
  //         <div style={{ border: '1px solid #cbd5e1', padding: '15px', backgroundColor: '#f8fafc' }}>
  //           <p style={{ margin: '0 0 12px 0', ...f('sectionHeaders'), letterSpacing: '1.5px', textTransform: 'uppercase' }}>BANK DETAILS</p>
  //           <div style={{ marginBottom: '8px' }}>
  //             <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Bank</p>
  //             <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.bankName}</p>
  //           </div>
  //           <div style={{ marginBottom: '8px' }}>
  //             <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Account Holder Name</p>
  //             <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.accountholdername}</p>
  //           </div>
  //           <div style={{ marginBottom: '8px' }}>
  //             <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>Account Number</p>
  //             <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.accountNumber}</p>
  //           </div>
  //           <div>
  //             <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>IFSC Code</p>
  //             <p style={{ margin: '2px 0 0 0', ...f('tableContent'), fontWeight: '600', fontSize: '13px' }}>{data.bankInfo?.ifscCode}</p>
  //           </div>
  //         </div>
  //       </div>

  //       <div style={{ width: '45%', display: 'flex', justifyContent: 'flex-end' }}>
  //         <div style={{ minWidth: '280px' }}>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //             <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
  //             <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.subtotal)}</span>
  //           </div>
  //           {data.cgst > 0 && (
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>CGST ({data.cgstPercent}%)</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.cgst)}</span>
  //             </div>
  //           )}
  //           {data.sgst > 0 && (
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>SGST ({data.sgstPercent}%)</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.sgst)}</span>
  //             </div>
  //           )}
  //           {data.igst > 0 && (
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>IGST</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.igst)}</span>
  //             </div>
  //           )}
  //           {data.otherCharges > 0 && (
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Other Charges</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(data.otherCharges)}</span>
  //             </div>
  //           )}
  //           {data.roundOff != null && data.roundOff !== 0 && (
  //             <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //               <span style={{ fontSize: '13px', color: '#64748b' }}>Round Off</span>
  //               <span style={{ fontSize: '13px', fontWeight: '600', color: data.roundOff < 0 ? '#dc2626' : '#1e293b' }}>{formatCurrency(data.roundOff)}</span>
  //             </div>
  //           )}
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>
  //             <span style={{ fontSize: '13px', color: '#64748b' }}>Discount</span>
  //             <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>₹{data.discount || '0.00'}</span>
  //           </div>
  //           <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', borderTop: '2px solid #334155', marginTop: '10px' }}>
  //             <span style={{ ...f('totals'), letterSpacing: '1px' }}>TOTAL</span>
  //             <span style={f('totals')}>{formatCurrency(data.total)}</span>
  //           </div>

  //           <div style={{
  //             marginTop: '16px',
  //             padding: '12px',
  //             backgroundColor: '#f8fafc',
  //             border: '1px dashed #cbd5e1',
  //             borderRadius: '6px',
  //             textAlign: 'center'
  //           }}>
  //             <span style={{
  //               fontSize: '14px',
  //               fontFamily: fonts.totals.family,
  //               fontStyle: 'italic',
  //               color: '#334155',
  //               fontWeight: '600',
  //               letterSpacing: '0.5px'
  //             }}>
  //               Amount in Words: {totalInWords}
  //             </span>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* Draggable Signature */}
  //     {/* Signature Section - Fixed at bottom with proper spacing */}
  //     <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '1px solid #e0e0e0' }}>
  //       {signatureImage ? (
  //         <div
  //           style={{
  //             position: 'relative',
  //             left: `${signaturePosition.x}px`,
  //             cursor: 'move',
  //             display: 'inline-block',
  //             border: '2px dashed transparent',
  //             transition: 'border 0.2s'
  //           }}
  //           draggable
  //           onDragEnd={onSignatureDrag}
  //           onMouseOver={(e) => e.currentTarget.style.border = '2px dashed #dc2626'}
  //           onMouseOut={(e) => e.currentTarget.style.border = '2px dashed transparent'}
  //         >
  //           <img src={signatureImage} alt="Signature" style={{ height: '80px' }} />
  //           <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#1e293b', fontWeight: '600' }}>
  //             Authorized Signatory
  //           </div>
  //         </div>
  //       ) : (
  //         <div style={{ textAlign: 'right' }}>
  //           <div style={{ display: 'inline-block', borderTop: '1px solid #333', paddingTop: '8px', minWidth: '200px', textAlign: 'center' }}>
  //             <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Authorized Signatory</p>
  //           </div>
  //         </div>
  //       )}
  //     </div>
  //   </div>
  // );
};


// const Template = () => {
//   const location = useLocation();
//   const previewRef = useRef(null);
//   const initialData = location.state?.invoiceData || null;

//   // ✅ FIX: Initialize fontSettings with default values instead of empty object
//   const defaultFontSettings = {
//     companyName: { size: 24, family: 'Arial', color: '#333333', fontWeight: 'normal' },
//     invoiceTitle: { size: 32, family: 'Arial', color: '#333333', fontWeight: 'bold' },
//     sectionHeaders: { size: 12, family: 'Arial', color: '#666666', fontWeight: 'bold' },
//     tableHeaders: { size: 11, family: 'Arial', color: '#000000', fontWeight: 'bold' },
//     tableContent: { size: 12, family: 'Arial', color: '#000000', fontWeight: 'normal' },
//     totals: { size: 14, family: 'Arial', color: '#333333', fontWeight: 'bold' }
//   };

//   // 1. Core Data and Templates
//   const [invoiceData, setInvoiceData] = useState(initialData);
//   const [selectedTemplate, setSelectedTemplate] = useState(
//     localStorage.getItem('selectedTemplate') || 'classic'
//   );
//   const dataToUse = transformInvoiceData(invoiceData);
//   let history = useHistory();


//   // 2. Customization States
//   const [logoPosition, setLogoPosition] = useState(
//     JSON.parse(localStorage.getItem('logoPosition') || '{"x": 40, "y": 40}')
//   );
//   const [signaturePosition, setSignaturePosition] = useState(
//     JSON.parse(localStorage.getItem('signaturePosition') || '{"x": 600, "y": 800}')
//   );
//   const [logoSize, setLogoSize] = useState(
//     parseInt(localStorage.getItem('logoSize') || 120)
//   );
//   const [signatureImage, setSignatureImage] = useState(
//     localStorage.getItem('signatureImage') || null
//   );
//   const [columnVisibility, setColumnVisibility] = useState(
//     JSON.parse(localStorage.getItem('columnVisibility') ||
//       '{"srNo": true, "item": true, "hsn": true, "qty": true, "rate": true, "gst": true, "discount": false, "amount": true}'
//     )
//   );
//   const [headerLabels, setHeaderLabels] = useState(
//     JSON.parse(localStorage.getItem('headerLabels') ||
//       '{"item": "Item & Description", "hsn": "HSN/SAC", "qty": "Qty", "rate": "Rate", "gst": "GST %", "discount": "Discount", "amount": "Amount"}'
//     )
//   );

//   // ✅ FIX: Initialize with default values or saved values
//   const [fontSettings, setFontSettings] = useState(() => {
//     const saved = localStorage.getItem('fontSettings');
//     if (saved) {
//       try {
//         const parsed = JSON.parse(saved);
//         // Ensure all required keys exist
//         return { ...defaultFontSettings, ...parsed };
//       } catch (e) {
//         return defaultFontSettings;
//       }
//     }
//     return defaultFontSettings;
//   });

//   const [columnWidths, setColumnWidths] = useState(
//     JSON.parse(localStorage.getItem('columnWidths') || '{}')
//   );

//   const [showCustomization, setShowCustomization] = useState(false);

//   // Helper to get user-friendly label for the selected template
//   const getTemplateLabel = (key) => {
//     const labels = {
//       classic: 'Classic',
//       modern: 'Modern',
//       bold: 'Bold',
//       elegant: 'Elegant',
//     };
//     return labels[key] || 'Classic';
//   };

//   // 3. Effects and Handlers
//   useEffect(() => {
//     localStorage.setItem('selectedTemplate', selectedTemplate);
//     localStorage.setItem('logoSize', logoSize);
//     localStorage.setItem('columnVisibility', JSON.stringify(columnVisibility));
//     localStorage.setItem('headerLabels', JSON.stringify(headerLabels));
//     localStorage.setItem('fontSettings', JSON.stringify(fontSettings));
//     localStorage.setItem('columnWidths', JSON.stringify(columnWidths));
//   }, [selectedTemplate, logoSize, columnVisibility, headerLabels, fontSettings, columnWidths]);

//   const handleDrag = (e, setPosition) => {
//     const rect = previewRef.current.getBoundingClientRect();
//     const newX = e.clientX - rect.left;
//     const newY = e.clientY - rect.top;

//     const finalX = Math.max(0, newX);
//     const finalY = Math.max(0, newY);

//     setPosition({ x: finalX, y: finalY });
//     localStorage.setItem(setPosition === setLogoPosition ? 'logoPosition' : 'signaturePosition', JSON.stringify({ x: finalX, y: finalY }));
//   };

//   const handleColumnResize = (key, width) => {
//     setColumnWidths(prev => ({ ...prev, [key]: `${width}px` }));
//   };

//   const downloadPDF = async () => {
//     const element = previewRef.current;
//     const canvas = await html2canvas(element, { scale: 2 });
//     const imgData = canvas.toDataURL('image/png');
//     const pdf = new jsPDF('p', 'mm', 'a4');
//     const width = pdf.internal.pageSize.getWidth();
//     const height = pdf.internal.pageSize.getHeight();

//     const imgProps = pdf.getImageProperties(imgData);
//     const imgWidth = width;
//     const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

//     let position = 0;
//     let remainingHeight = imgHeight;

//     while (remainingHeight > 0) {
//       if (position !== 0) {
//         pdf.addPage();
//       }
//       pdf.addImage(imgData, 'PNG', 0, position * -1, width, imgHeight, undefined, 'FAST');
//       remainingHeight -= height;
//       position += height;
//     }

//     pdf.save(`invoice-${dataToUse.invoice.number}.pdf`);
//   };

//   const handleSignatureUpload = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onload = (ev) => setSignatureImage(ev.target.result);
//       reader.readAsDataURL(file);
//     }
//   };

//   // 4. Return Statement
//   return (
//     <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif' }}>

//       {/* LEFT PANEL - CONTROLS */}
//       <div
//         style={{
//           width: showCustomization ? '340px' : '60px',
//           minWidth: showCustomization ? '340px' : '60px',
//           backgroundColor: '#f8fafc',
//           borderRight: '1px solid #ddd',
//           overflowY: showCustomization ? 'auto' : 'hidden',
//           transition: 'all 0.3s ease-in-out',
//           position: 'relative',
//           padding: showCustomization ? '20px' : '0',
//         }}
//       >
//         {/* Configuration Button */}
//         <button
//           onClick={() => setShowCustomization(prev => !prev)}
//           title={showCustomization ? 'Hide Customization Panel' : 'Show Customization Panel'}
//           style={{
//             position: 'absolute',
//             top: '20px',
//             right: showCustomization ? '20px' : '10px',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             padding: showCustomization ? '8px 12px' : '10px',
//             borderRadius: '8px',
//             border: '1px solid #cbd5e1',
//             backgroundColor: showCustomization ? '#1e40af' : 'white',
//             color: showCustomization ? 'white' : '#475569',
//             cursor: 'pointer',
//             fontSize: '14px',
//             fontWeight: '600',
//             boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
//             transition: 'all 0.3s ease',
//             zIndex: 10,
//             width: showCustomization ? 'auto' : '40px',
//             height: showCustomization ? 'auto' : '40px',
//             flexShrink: 0,
//           }}
//         >
//           <span style={{
//             fontSize: '18px',
//             marginRight: showCustomization ? '8px' : '0',
//             transform: showCustomization ? 'rotate(0deg)' : 'rotate(90deg)',
//             transition: 'transform 0.3s ease'
//           }}>&#x2699;</span>

//           {showCustomization && <span style={{ whiteSpace: 'nowrap' }}>Hide Customization</span>}

//           {!showCustomization && (
//             <div style={{ position: 'absolute', top: '55px', left: '0', width: '100%', textAlign: 'center', fontSize: '10px', color: '#475569', fontWeight: 'normal' }}>
//               {getTemplateLabel(selectedTemplate)}
//             </div>
//           )}
//         </button>

//         {/* CUSTOMIZATION CONTENT */}
//         {showCustomization && (
//           <div style={{ width: '340px', backgroundColor: '#f8fafc', padding: '20px', borderRight: '1px solid #ddd', overflowY: 'auto' }}>
//             <h2 style={{ fontSize: '20px', marginBottom: '20px', marginTop: '30px', color: '#1e293b' }}> Customize Invoice </h2>

//             {/* TEMPLATE SELECTOR */}
//             <div style={{ marginBottom: '28px', padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
//               <label style={{ fontWeight: '600', display: 'block', marginBottom: '15px', fontSize: '14px', color: '#334155' }}> Select Template Style </label>
//               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
//                 {/* Classic */}
//                 <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'classic' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'classic' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'classic') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'classic') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
//                   <input type="radio" name="template" value="classic" checked={selectedTemplate === 'classic'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
//                   <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'classic' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Classic </div>
//                   <div style={{ fontSize: '11px', color: '#94a3b8' }}>Standard & Formal</div>
//                 </label>

//                 {/* Modern */}
//                 <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'modern' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'modern' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'modern') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'modern') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
//                   <input type="radio" name="template" value="modern" checked={selectedTemplate === 'modern'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
//                   <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'modern' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Modern </div>
//                   <div style={{ fontSize: '11px', color: '#94a3b8' }}>Clean & Minimal</div>
//                 </label>

//                 {/* Bold */}
//                 <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'bold' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'bold' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'bold') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'bold') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
//                   <input type="radio" name="template" value="bold" checked={selectedTemplate === 'bold'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
//                   <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'bold' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Bold </div>
//                   <div style={{ fontSize: '11px', color: '#94a3b8' }}>Professional & Strong</div>
//                 </label>

//                 {/* Elegant */}
//                 <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'elegant' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'elegant' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'elegant') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'elegant') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
//                   <input type="radio" name="template" value="elegant" checked={selectedTemplate === 'elegant'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
//                   <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'elegant' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Elegant </div>
//                   <div style={{ fontSize: '11px', color: '#94a3b8' }}>Refined & Minimalist</div>
//                 </label>
//               </div>
//             </div>

//             {/* Upload Signature */}
//             <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
//               <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px', fontSize: '14px', color: '#334155' }}> Upload Digital Signature </label>
//               <input type="file" accept="image/*" onChange={handleSignatureUpload} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }} />
//               {signatureImage && (
//                 <div style={{ marginTop: '10px', textAlign: 'center' }}>
//                   <img src={signatureImage} alt="Signature Preview" style={{ maxHeight: '60px', border: '1px solid #e2e8f0', borderRadius: '4px' }} />
//                   <p style={{ fontSize: '11px', color: '#64748b', marginTop: '5px' }}>Drag signature on invoice to reposition</p>
//                 </div>
//               )}
//             </div>

//             {/* Logo Size */}
//             <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
//               <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px', fontSize: '14px', color: '#334155' }}> Logo Size: {logoSize}px </label>
//               <input type="range" min="60" max="180" value={logoSize} onChange={(e) => setLogoSize(e.target.value)} style={{ width: '100%' }} />
//               <p style={{ fontSize: '11px', color: '#64748b', marginTop: '8px' }}>Drag logo on invoice to reposition</p>
//             </div>

//             {/* Column Visibility */}
//             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
//               <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Column Visibility</h4>
//               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
//                 {Object.keys(columnVisibility).map(key => (
//                   <label key={key} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#334155' }}>
//                     <input
//                       type="checkbox"
//                       checked={columnVisibility[key]}
//                       onChange={(e) => setColumnVisibility(prev => ({ ...prev, [key]: e.target.checked }))}
//                       style={{ marginRight: '8px' }}
//                     />
//                     {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
//                   </label>
//                 ))}
//               </div>
//             </div>

//             {/* Column Labels */}
//             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
//               <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Column Headers</h4>
//               {Object.keys(headerLabels).map(key => (
//                 <div key={key} style={{ marginBottom: '15px' }}>
//                   <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px', textTransform: 'capitalize' }}>
//                     {key === 'item' ? 'Item/Description' : key === 'hsn' ? 'HSN' : key === 'qty' ? 'Quantity' : key === 'rate' ? 'Rate' : key === 'gst' ? 'GST' : key === 'discount' ? 'Discount' : 'Amount'}
//                   </label>
//                   <input type="text" value={headerLabels[key]} onChange={(e) => setHeaderLabels(prev => ({ ...prev, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }} />
//                 </div>
//               ))}
//             </div>

//             {/* ✅ FIX: Font Settings Section - Now properly displays */}
//             <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
//               <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Font Settings</h4>

//               {Object.keys(fontSettings).map(key => (
//                 <div key={key} style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
//                   <label style={{ display: 'block', fontSize: '13px', color: '#334155', marginBottom: '8px', fontWeight: '600', textTransform: 'capitalize' }}>
//                     {key.replace(/([A-Z])/g, ' $1').trim()}
//                   </label>

//                   {/* Size */}
//                   <div style={{ marginBottom: '10px' }}>
//                     <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Size (px)</label>
//                     <select
//                       value={fontSettings[key].size}
//                       onChange={e => setFontSettings(prev => ({
//                         ...prev,
//                         [key]: { ...prev[key], size: parseInt(e.target.value) }
//                       }))}
//                       style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
//                     >
//                       {[8, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 36, 40, 48].map(s => (
//                         <option key={s} value={s}>{s}px</option>
//                       ))}
//                     </select>
//                   </div>

//                   {/* Family */}
//                   <div style={{ marginBottom: '10px' }}>
//                     <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
//                       Font Family (Works in Microsoft Word & Windows)
//                     </label>
//                     <select
//                       value={fontSettings[key].family}
//                       onChange={e => setFontSettings(prev => ({
//                         ...prev,
//                         [key]: { ...prev[key], family: e.target.value }
//                       }))}
//                       style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
//                     >
//                       <option value="Arial">Arial (Clean sans-serif – default)</option>
//                       <option value="Arial Black">Arial Black (Extra bold)</option>
//                       <option value="Calibri">Calibri (Modern Windows default)</option>
//                       <option value="Cambria">Cambria (Professional serif)</option>
//                       <option value="Candara">Candara (Friendly rounded)</option>
//                       <option value="Constantia">Constantia (Elegant serif)</option>
//                       <option value="Corbel">Corbel (Clean & modern)</option>
//                       <option value="Consolas">Consolas (Best coding/monospace)</option>
//                       <option value="Courier New">Courier New (Typewriter look)</option>
//                       <option value="Georgia">Georgia (Warm readable serif)</option>
//                       <option value="Impact">Impact (Meme-style bold)</option>
//                       <option value="Lucida Console">Lucida Console (Classic monospace)</option>
//                       <option value="Segoe UI">Segoe UI (Windows 10/11 interface font)</option>
//                       <option value="Tahoma">Tahoma (Classic Windows font)</option>
//                       <option value="Times New Roman">Times New Roman (Traditional reports)</option>
//                       <option value="Trebuchet MS">Trebuchet MS (Playful sans-serif)</option>
//                       <option value="Verdana">Verdana (Very readable on screen)</option>
//                       <option value="Wingdings">Wingdings (Symbols/Icons – fun!)</option>
//                     </select>
//                   </div>

//                   {/* Weight */}
//                   <div style={{ marginBottom: '10px' }}>
//                     <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Font Weight</label>
//                     <select
//                       value={fontSettings[key].fontWeight}
//                       onChange={e => setFontSettings(prev => ({
//                         ...prev,
//                         [key]: { ...prev[key], fontWeight: e.target.value }
//                       }))}
//                       style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
//                     >
//                       <option value="normal">Normal</option>
//                       <option value="bold">Bold</option>
//                       <option value="300">300 (Light)</option>
//                       <option value="400">400 (Regular)</option>
//                       <option value="500">500 (Medium)</option>
//                       <option value="600">600 (Semi Bold)</option>
//                       <option value="700">700 (Bold)</option>
//                     </select>
//                   </div>

//                   {/* Color */}
//                   <div>
//                     <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Color</label>
//                     <div style={{ display: 'flex', gap: '8px' }}>
//                       <input
//                         type="color"
//                         value={fontSettings[key].color}
//                         onChange={e => setFontSettings(prev => ({
//                           ...prev,
//                           [key]: { ...prev[key], color: e.target.value }
//                         }))}
//                         style={{ width: '50px', height: '40px', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}
//                       />
//                       <input
//                         type="text"
//                         value={fontSettings[key].color}
//                         onChange={e => setFontSettings(prev => ({
//                           ...prev,
//                           [key]: { ...prev[key], color: e.target.value }
//                         }))}
//                         style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace' }}
//                       />
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>

//           </div>
//         )}
//       </div>

//       {/* RIGHT PANEL - PREVIEW */}
//       <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>

//         <div style={{ maxWidth: '800px', margin: '0 auto 20px', display: 'flex', justifyContent: 'flex-end' }}>
//           <button
//             onClick={() => history.goBack()}
//             style={{
//               padding: '12px 24px',
//               backgroundColor: '#2563eb',
//               color: 'white',
//               border: 'none',
//               borderRadius: '8px',
//               fontSize: '14px',
//               fontWeight: '600',
//               cursor: 'pointer',
//               transition: 'all 0.2s ease',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '8px',
//               boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
//               marginRight: '5px'
//             }}
//             onMouseOver={(e) => {
//               e.currentTarget.style.backgroundColor = '#1e40af';
//               e.currentTarget.style.transform = 'translateY(-1px)';
//               e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
//             }}
//             onMouseOut={(e) => {
//               e.currentTarget.style.backgroundColor = '#2563eb';
//               e.currentTarget.style.transform = 'translateY(0)';
//               e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
//             }}
//           >
//             <svg
//               width="16"
//               height="16"
//               viewBox="0 0 24 24"
//               fill="none"
//               stroke="currentColor"
//               strokeWidth="2"
//               strokeLinecap="round"
//               strokeLinejoin="round"
//             >
//               {/* Proper left arrow (chevron-left) icon */}
//               <path d="M19 12H5" />
//               <polyline points="12 19 5 12 12 5" />
//             </svg>
//             Back
//           </button>
//           <button
//             onClick={downloadPDF}
//             style={{
//               padding: '12px 24px',
//               backgroundColor: '#2563eb',
//               color: 'white',
//               border: 'none',
//               borderRadius: '8px',
//               fontSize: '14px',
//               fontWeight: '600',
//               cursor: 'pointer',
//               transition: 'all 0.2s ease',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '8px',
//               boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
//             }}
//             onMouseOver={(e) => {
//               e.currentTarget.style.backgroundColor = '#1e40af';
//               e.currentTarget.style.transform = 'translateY(-1px)';
//               e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
//             }}
//             onMouseOut={(e) => {
//               e.currentTarget.style.backgroundColor = '#2563eb';
//               e.currentTarget.style.transform = 'translateY(0)';
//               e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
//             }}
//           >
//             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//               <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
//               <polyline points="7 10 12 15 17 10"></polyline>
//               <line x1="12" y1="15" x2="12" y2="3"></line>
//             </svg>
//             Download PDF
//           </button>

//         </div>

//         <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }} ref={previewRef}>
//           {selectedTemplate === 'classic' && (
//             <ClassicTemplate
//               data={dataToUse}
//               columnVisibility={columnVisibility}
//               headerLabels={headerLabels}
//               logoSize={logoSize}
//               logoPosition={logoPosition}
//               signatureImage={signatureImage}
//               signaturePosition={signaturePosition}
//               fontSettings={fontSettings}
//               columnWidths={columnWidths}
//               onColumnResize={handleColumnResize}
//               onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
//               onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
//             />
//           )}

//           {selectedTemplate === 'modern' && (
//             <ModernTemplate
//               data={dataToUse}
//               columnVisibility={columnVisibility}
//               headerLabels={headerLabels}
//               logoSize={logoSize}
//               logoPosition={logoPosition}
//               signatureImage={signatureImage}
//               signaturePosition={signaturePosition}
//               fontSettings={fontSettings}
//               columnWidths={columnWidths}
//               onColumnResize={handleColumnResize}
//               onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
//               onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
//             />
//           )}
//           {selectedTemplate === 'bold' && (
//             <BoldTemplate
//               data={dataToUse}
//               columnVisibility={columnVisibility}
//               headerLabels={headerLabels}
//               logoSize={logoSize}
//               logoPosition={logoPosition}
//               signatureImage={signatureImage}
//               signaturePosition={signaturePosition}
//               fontSettings={fontSettings}
//               columnWidths={columnWidths}
//               onColumnResize={handleColumnResize}
//               onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
//               onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
//             />
//           )}
//           {selectedTemplate === 'elegant' && (
//             <ElegantTemplate
//               data={dataToUse}
//               columnVisibility={columnVisibility}
//               headerLabels={headerLabels}
//               logoSize={logoSize}
//               logoPosition={logoPosition}
//               signatureImage={signatureImage}
//               signaturePosition={signaturePosition}
//               fontSettings={fontSettings}
//               columnWidths={columnWidths}
//               onColumnResize={handleColumnResize}
//               onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
//               onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
//             />
//           )}
//           {!['classic', 'modern', 'bold', 'elegant'].includes(selectedTemplate) && (
//             <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
//               <p>Please select a template to preview.</p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

const Template = () => {
  const location = useLocation();
  const previewRef = useRef(null);
  const initialData = location.state?.invoiceData || null;
  // ✅ FIX: Initialize fontSettings with default values instead of empty object
  const defaultFontSettings = {
    companyName: { size: 24, family: 'Arial', color: '#333333', fontWeight: 'normal' },
    invoiceTitle: { size: 32, family: 'Arial', color: '#333333', fontWeight: 'bold' },
    sectionHeaders: { size: 12, family: 'Arial', color: '#666666', fontWeight: 'bold' },
    tableHeaders: { size: 11, family: 'Arial', color: '#000000', fontWeight: 'bold' },
    tableContent: { size: 12, family: 'Arial', color: '#000000', fontWeight: 'normal' },
    totals: { size: 14, family: 'Arial', color: '#333333', fontWeight: 'bold' }
  };
  // 1. Core Data and Templates
  const [invoiceData, setInvoiceData] = useState(initialData);
  const [selectedTemplate, setSelectedTemplate] = useState(
    localStorage.getItem('selectedTemplate') || 'classic'
  );
  const dataToUse = transformInvoiceData(invoiceData);
  let history = useHistory();
  // 2. Customization States
  const [logoPosition, setLogoPosition] = useState(
    JSON.parse(localStorage.getItem('logoPosition') || '{"x": 40, "y": 40}')
  );
  const [signaturePosition, setSignaturePosition] = useState(
    JSON.parse(localStorage.getItem('signaturePosition') || '{"x": 600, "y": 800}')
  );
  const [logoSize, setLogoSize] = useState(
    parseInt(localStorage.getItem('logoSize') || 120)
  );
  const [signatureImage, setSignatureImage] = useState(
    localStorage.getItem('signatureImage') || null
  );
  const [columnVisibility, setColumnVisibility] = useState(
    JSON.parse(localStorage.getItem('columnVisibility') ||
      '{"srNo": true, "item": true, "hsn": true, "qty": true, "rate": true, "gst": true, "discount": false, "amount": true}'
    )
  );
  const [headerLabels, setHeaderLabels] = useState(
    JSON.parse(localStorage.getItem('headerLabels') ||
      '{"item": "Item & Description", "hsn": "HSN/SAC", "qty": "Qty", "rate": "Rate", "gst": "GST %", "discount": "Discount", "amount": "Amount"}'
    )
  );
  // ✅ FIX: Initialize with default values or saved values
  const [fontSettings, setFontSettings] = useState(() => {
    const saved = localStorage.getItem('fontSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure all required keys exist
        return { ...defaultFontSettings, ...parsed };
      } catch (e) {
        return defaultFontSettings;
      }
    }
    return defaultFontSettings;
  });
  const [columnWidths, setColumnWidths] = useState(
    JSON.parse(localStorage.getItem('columnWidths') || '{}')
  );
  const [showCustomization, setShowCustomization] = useState(false);
  // Helper to get user-friendly label for the selected template
  const getTemplateLabel = (key) => {
    const labels = {
      classic: 'Classic',
      modern: 'Modern',
      bold: 'Bold',
      elegant: 'Elegant',
    };
    return labels[key] || 'Classic';
  };
  // 3. Effects and Handlers
  useEffect(() => {
    localStorage.setItem('selectedTemplate', selectedTemplate);
    localStorage.setItem('logoSize', logoSize);
    localStorage.setItem('columnVisibility', JSON.stringify(columnVisibility));
    localStorage.setItem('headerLabels', JSON.stringify(headerLabels));
    localStorage.setItem('fontSettings', JSON.stringify(fontSettings));
    localStorage.setItem('columnWidths', JSON.stringify(columnWidths));
  }, [selectedTemplate, logoSize, columnVisibility, headerLabels, fontSettings, columnWidths]);
  const handleDrag = (e, setPosition) => {
    const rect = previewRef.current.getBoundingClientRect();
    const newX = e.clientX - rect.left;
    const newY = e.clientY - rect.top;
    const finalX = Math.max(0, newX);
    const finalY = Math.max(0, newY);
    setPosition({ x: finalX, y: finalY });
    localStorage.setItem(setPosition === setLogoPosition ? 'logoPosition' : 'signaturePosition', JSON.stringify({ x: finalX, y: finalY }));
  };
  const handleColumnResize = (key, width) => {
    setColumnWidths(prev => ({ ...prev, [key]: `${width}px` }));
  };
  const downloadPDF = async () => {
    const element = previewRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = width;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
    let position = 0;
    let remainingHeight = imgHeight;
    while (remainingHeight > 0) {
      if (position !== 0) {
        pdf.addPage();
      }
      pdf.addImage(imgData, 'PNG', 0, position * -1, width, imgHeight, undefined, 'FAST');
      remainingHeight -= height;
      position += height;
    }
    pdf.save(`invoice-${dataToUse.invoice.number}.pdf`);
  };
  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setSignatureImage(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // NEW: Save Template Button Handler (you can extend this later if needed)
  const handleSaveTemplate = () => {
    localStorage.setItem('selectedTemplate', selectedTemplate);
    localStorage.setItem('logoSize', logoSize);
    localStorage.setItem('logoPosition', JSON.stringify(logoPosition));
    localStorage.setItem('signaturePosition', JSON.stringify(signaturePosition));
    localStorage.setItem('signatureImage', signatureImage || '');
    localStorage.setItem('columnVisibility', JSON.stringify(columnVisibility));
    localStorage.setItem('headerLabels', JSON.stringify(headerLabels));
    localStorage.setItem('fontSettings', JSON.stringify(fontSettings));
    localStorage.setItem('columnWidths', JSON.stringify(columnWidths));

    alert('Template settings saved successfully!');
  };

  // 4. Return Statement
  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
      {/* LEFT PANEL - CONTROLS */}
      <div
        style={{
          width: showCustomization ? '340px' : '60px',
          minWidth: showCustomization ? '340px' : '60px',
          backgroundColor: '#f8fafc',
          borderRight: '1px solid #ddd',
          overflowY: showCustomization ? 'auto' : 'hidden',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          padding: showCustomization ? '20px' : '0',
        }}
      >
        {/* Configuration Button */}
        <button
          onClick={() => setShowCustomization(prev => !prev)}
          title={showCustomization ? 'Hide Customization Panel' : 'Show Customization Panel'}
          style={{
            position: 'absolute',
            top: '20px',
            right: showCustomization ? '20px' : '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: showCustomization ? '8px 12px' : '10px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: showCustomization ? '#1e40af' : 'white',
            color: showCustomization ? 'white' : '#475569',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            zIndex: 10,
            width: showCustomization ? 'auto' : '40px',
            height: showCustomization ? 'auto' : '40px',
            flexShrink: 0,
          }}
        >
          <span style={{
            fontSize: '18px',
            marginRight: showCustomization ? '8px' : '0',
            transform: showCustomization ? 'rotate(0deg)' : 'rotate(90deg)',
            transition: 'transform 0.3s ease'
          }}>&#x2699;</span>
          {showCustomization && <span style={{ whiteSpace: 'nowrap' }}>Hide Customization</span>}
          {!showCustomization && (
            <div style={{ position: 'absolute', top: '55px', left: '0', width: '100%', textAlign: 'center', fontSize: '10px', color: '#475569', fontWeight: 'normal' }}>
              {getTemplateLabel(selectedTemplate)}
            </div>
          )}
        </button>
        {/* CUSTOMIZATION CONTENT */}
        {showCustomization && (
          <div style={{ width: '340px', backgroundColor: '#f8fafc', padding: '20px', borderRight: '1px solid #ddd', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '20px', marginBottom: '20px', marginTop: '30px', color: '#1e293b' }}> Customize Invoice </h2>
            {/* TEMPLATE SELECTOR */}
            <div style={{ marginBottom: '28px', padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '15px', fontSize: '14px', color: '#334155' }}> Select Template Style </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {/* Classic */}
                <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'classic' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'classic' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'classic') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'classic') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
                  <input type="radio" name="template" value="classic" checked={selectedTemplate === 'classic'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
                  <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'classic' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Classic </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Standard & Formal</div>
                </label>
                {/* Modern */}
                <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'modern' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'modern' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'modern') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'modern') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
                  <input type="radio" name="template" value="modern" checked={selectedTemplate === 'modern'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
                  <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'modern' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Modern </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Clean & Minimal</div>
                </label>
                {/* Bold */}
                <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'bold' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'bold' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'bold') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'bold') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
                  <input type="radio" name="template" value="bold" checked={selectedTemplate === 'bold'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
                  <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'bold' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Bold </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Professional & Strong</div>
                </label>
                {/* Elegant */}
                <label style={{ cursor: 'pointer', padding: '18px 16px', textAlign: 'center', borderRadius: '10px', border: '2px solid', borderColor: selectedTemplate === 'elegant' ? '#1e40af' : '#cbd5e1', backgroundColor: selectedTemplate === 'elegant' ? '#eff6ff' : 'white', transition: 'all 0.25s ease', userSelect: 'none', }} onMouseEnter={(e) => { if (selectedTemplate !== 'elegant') { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.backgroundColor = '#f8faff'; } }} onMouseLeave={(e) => { if (selectedTemplate !== 'elegant') { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = 'white'; } }} >
                  <input type="radio" name="template" value="elegant" checked={selectedTemplate === 'elegant'} onChange={(e) => setSelectedTemplate(e.target.value)} style={{ display: 'none' }} />
                  <div style={{ fontWeight: '600', fontSize: '15px', color: selectedTemplate === 'elegant' ? '#1e40af' : '#475569', marginBottom: '4px' }}> Elegant </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Refined & Minimalist</div>
                </label>
              </div>
            </div>
            {/* Upload Signature */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px', fontSize: '14px', color: '#334155' }}> Upload Digital Signature </label>
              <input type="file" accept="image/*" onChange={handleSignatureUpload} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }} />
              {signatureImage && (
                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                  <img src={signatureImage} alt="Signature Preview" style={{ maxHeight: '60px', border: '1px solid #e2e8f0', borderRadius: '4px' }} />
                  <p style={{ fontSize: '11px', color: '#64748b', marginTop: '5px' }}>Drag signature on invoice to reposition</p>
                </div>
              )}
            </div>
            {/* Logo Size */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '10px', fontSize: '14px', color: '#334155' }}> Logo Size: {logoSize}px </label>
              <input type="range" min="60" max="180" value={logoSize} onChange={(e) => setLogoSize(e.target.value)} style={{ width: '100%' }} />
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '8px' }}>Drag logo on invoice to reposition</p>
            </div>
            {/* Column Visibility */}
            <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Column Visibility</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {Object.keys(columnVisibility).map(key => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={columnVisibility[key]}
                      onChange={(e) => setColumnVisibility(prev => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginRight: '8px' }}
                    />
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </label>
                ))}
              </div>
            </div>
            {/* Column Labels */}
            <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Column Headers</h4>
              {Object.keys(headerLabels).map(key => (
                <div key={key} style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px', textTransform: 'capitalize' }}>
                    {key === 'item' ? 'Item/Description' : key === 'hsn' ? 'HSN' : key === 'qty' ? 'Quantity' : key === 'rate' ? 'Rate' : key === 'gst' ? 'GST' : key === 'discount' ? 'Discount' : 'Amount'}
                  </label>
                  <input type="text" value={headerLabels[key]} onChange={(e) => setHeaderLabels(prev => ({ ...prev, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }} />
                </div>
              ))}
            </div>
            {/* ✅ FIX: Font Settings Section - Now properly displays */}
            <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#334155' }}>Font Settings</h4>
              {Object.keys(fontSettings).map(key => (
                <div key={key} style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#334155', marginBottom: '8px', fontWeight: '600', textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  {/* Size */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Size (px)</label>
                    <select
                      value={fontSettings[key].size}
                      onChange={e => setFontSettings(prev => ({
                        ...prev,
                        [key]: { ...prev[key], size: parseInt(e.target.value) }
                      }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    >
                      {[8, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 36, 40, 48].map(s => (
                        <option key={s} value={s}>{s}px</option>
                      ))}
                    </select>
                  </div>
                  {/* Family */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                      Font Family (Works in Microsoft Word & Windows)
                    </label>
                    <select
                      value={fontSettings[key].family}
                      onChange={e => setFontSettings(prev => ({
                        ...prev,
                        [key]: { ...prev[key], family: e.target.value }
                      }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="Arial">Arial (Clean sans-serif – default)</option>
                      <option value="Arial Black">Arial Black (Extra bold)</option>
                      <option value="Calibri">Calibri (Modern Windows default)</option>
                      <option value="Cambria">Cambria (Professional serif)</option>
                      <option value="Candara">Candara (Friendly rounded)</option>
                      <option value="Constantia">Constantia (Elegant serif)</option>
                      <option value="Corbel">Corbel (Clean & modern)</option>
                      <option value="Consolas">Consolas (Best coding/monospace)</option>
                      <option value="Courier New">Courier New (Typewriter look)</option>
                      <option value="Georgia">Georgia (Warm readable serif)</option>
                      <option value="Impact">Impact (Meme-style bold)</option>
                      <option value="Lucida Console">Lucida Console (Classic monospace)</option>
                      <option value="Segoe UI">Segoe UI (Windows 10/11 interface font)</option>
                      <option value="Tahoma">Tahoma (Classic Windows font)</option>
                      <option value="Times New Roman">Times New Roman (Traditional reports)</option>
                      <option value="Trebuchet MS">Trebuchet MS (Playful sans-serif)</option>
                      <option value="Verdana">Verdana (Very readable on screen)</option>
                      <option value="Wingdings">Wingdings (Symbols/Icons – fun!)</option>
                    </select>
                  </div>
                  {/* Weight */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Font Weight</label>
                    <select
                      value={fontSettings[key].fontWeight}
                      onChange={e => setFontSettings(prev => ({
                        ...prev,
                        [key]: { ...prev[key], fontWeight: e.target.value }
                      }))}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="300">300 (Light)</option>
                      <option value="400">400 (Regular)</option>
                      <option value="500">500 (Medium)</option>
                      <option value="600">600 (Semi Bold)</option>
                      <option value="700">700 (Bold)</option>
                    </select>
                  </div>
                  {/* Color */}
                  <div>
                    <label style={{ fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Color</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="color"
                        value={fontSettings[key].color}
                        onChange={e => setFontSettings(prev => ({
                          ...prev,
                          [key]: { ...prev[key], color: e.target.value }
                        }))}
                        style={{ width: '50px', height: '40px', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={fontSettings[key].color}
                        onChange={e => setFontSettings(prev => ({
                          ...prev,
                          [key]: { ...prev[key], color: e.target.value }
                        }))}
                        style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* RIGHT PANEL - PREVIEW */}
      <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto 30px', display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
          {/* Back Button */}
          <button
            onClick={() => history.goBack()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6b7280', // Gray for Back
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#4b5563';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#6b7280';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>

          {/* Save Template Button */}
          <button
            onClick={handleSaveTemplate}
            style={{
              padding: '12px 24px',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            Save Template
          </button>

          {/* Download PDF Button */}
          <button
            onClick={downloadPDF}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#1e40af';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF
          </button>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }} ref={previewRef}>
          {selectedTemplate === 'classic' && (
            <ClassicTemplate
              data={dataToUse}
              columnVisibility={columnVisibility}
              headerLabels={headerLabels}
              logoSize={logoSize}
              logoPosition={logoPosition}
              signatureImage={signatureImage}
              signaturePosition={signaturePosition}
              fontSettings={fontSettings}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
              onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
            />
          )}
          {selectedTemplate === 'modern' && (
            <ModernTemplate
              data={dataToUse}
              columnVisibility={columnVisibility}
              headerLabels={headerLabels}
              logoSize={logoSize}
              logoPosition={logoPosition}
              signatureImage={signatureImage}
              signaturePosition={signaturePosition}
              fontSettings={fontSettings}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
              onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
            />
          )}
          {selectedTemplate === 'bold' && (
            <BoldTemplate
              data={dataToUse}
              columnVisibility={columnVisibility}
              headerLabels={headerLabels}
              logoSize={logoSize}
              logoPosition={logoPosition}
              signatureImage={signatureImage}
              signaturePosition={signaturePosition}
              fontSettings={fontSettings}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
              onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
            />
          )}
          {selectedTemplate === 'elegant' && (
            <ElegantTemplate
              data={dataToUse}
              columnVisibility={columnVisibility}
              headerLabels={headerLabels}
              logoSize={logoSize}
              logoPosition={logoPosition}
              signatureImage={signatureImage}
              signaturePosition={signaturePosition}
              fontSettings={fontSettings}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              onLogoDrag={(e) => handleDrag(e, setLogoPosition)}
              onSignatureDrag={(e) => handleDrag(e, setSignaturePosition)}
            />
          )}
          {!['classic', 'modern', 'bold', 'elegant'].includes(selectedTemplate) && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <p>Please select a template to preview.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Template;